import i18next, { type TFunction } from 'i18next'
import { termsFor } from './terms.js'
import { featureTermsFor } from './feature-terms.js'
import { spellPhrasesFor } from './spell-phrases.js'
import { equipmentNamesFor, slotOptionsFor, slotTitlesFor } from './slots.js'

/**
 * i18next с первого ключа (D11): строка в коде — всегда ключ, никогда
 * текст. Языки ru и en; язык определяется по языку браузера, пока
 * профиля пользователя нет (он появится с регистрацией, фаза 3).
 *
 * Оба словаря лежат рядом и правятся вместе. Расхождение ловит тест
 * (`i18n.test.ts`): ключ, добавленный в один язык и забытый в другом,
 * иначе всплывёт у пользователя английской строкой посреди русской
 * страницы — а заметит это не разработчик.
 *
 * Названия записей SRD — термины: классы, подклассы, виды,
 * предыстории, навыки, языки, названия черт — переводятся словарём
 * `terms.ts` и попадают сюда под ключ `content.<ключ записи>`.
 * Названия особенностей внутри записей — словарём `feature-terms.ts`
 * под ключ `feature.<ключ особенности>`: «Второе дыхание» вместо
 * `Second Wind` под русским «Воином».
 *
 * Описания особенностей приходят с сервера переводом самой записи
 * (`content/translations`), а описания заклинаний пока английские:
 * это отдельная работа (open-questions, раздел 7).
 *
 * Заголовки слотов создания и варианты без записи (линия эльфа,
 * цвет драконьего наследия) — ветки `slot.*` и `slotOption.*`
 * из `slots.ts`; позиции стартовых наборов, у которых название
 * не совпадает с записью предмета, — ветка `equipment.*` оттуда же.
 *
 * Ключи движка (`save.strength`, `armor.light`, `skill.athletics`)
 * переводятся ветками `trait.*`. Это наши идентификаторы, а не контент,
 * и на экран они попадать не должны ни в каком виде: проверяется
 * тестом, который проходит по вкладке и падает на любом машинном
 * ключе.
 */

const ru = {
  transfer: {
    // Файл персонажа (D54).
    exportHint:
      'В файле — журнал решений, а не посчитанные числа: лист соберётся заново при импорте. Текстовый лист обратно не загружается.',
    import: 'Загрузить персонажа из файла',
    importHint: 'Файл, выгруженный этим же приложением.',
    tooLarge: 'Файл слишком большой: это не файл персонажа.',
    refused: 'Файл {{file}} не принят:',
    adopt: 'Завести эти записи у себя и импортировать ({{count}})',
    imported: 'Персонаж загружен.',
    // Меню «Экспортировать» на листе (D54, D64).
    menu: 'Экспортировать',
    toDevice: 'На другое устройство',
    toDeviceHint: 'Файл персонажа: загрузите его на другом устройстве.',
    toText: 'Текстом',
    toTextHint: 'Лист в Markdown — для заметок, обратно не загружается.',
    toPrint: 'Распечатать',
    toPrintHint: 'Бланк A4 на одну страницу.',
  },
  // Печатный лист (D64). Подписи, которые есть на экране, берутся
  // оттуда же (`sheet.*`, `combat.*`, `spells.*`): здесь только то,
  // чего на экране нет.
  print: {
    title: 'Печатный лист',
    hint: 'Одна страница A4. Поля и масштаб лист задаёт сам — в окне печати их менять не нужно. Нужен файл, а не бумага, — выберите там «Сохранить как PDF».',
    print: 'Распечатать',
    back: 'К листу',
    loading: 'Собираем лист…',
    classLevel: '{{name}} {{level}}',
    levelOnly: 'Уровень {{level}}',
    xpOf: '{{total}} / {{next}}',
    save: 'Спасбросок',
    more: 'ещё {{count}} — в приложении',
    label: {
      name: 'Имя персонажа',
      classLevel: 'Класс и уровень',
      subclass: 'Подкласс',
      species: 'Вид',
      background: 'Предыстория',
      originFeat: 'Черта происхождения',
      player: 'Имя игрока',
      alignment: 'Мировоззрение',
      xp: 'Опыт / до уровня',
      inspiration: 'Героическое вдохновение',
      hpMax: 'Максимум',
      remaining: 'Осталось',
      total: 'Всего',
      spent: 'Потрачено',
      deathSaves: 'Спасброски от смерти',
      failures: 'Провалы',
      successes: 'Успехи',
      attackName: 'Название',
      damageType: 'Тип урона',
      spellAbility: 'Характеристика заклинаний',
      saveDcHint: '8 + бонус мастерства + модификатор',
      attackHint: 'бонус мастерства + модификатор',
      resources: 'Счётчики',
      notes: 'Заметки',
    },
    coin: {
      cp: 'ММ',
      sp: 'СМ',
      ep: 'ЭМ',
      gp: 'ЗМ',
      pp: 'ПМ',
    },
    icons: 'Иконки: game-icons.net — Delapouite, Lorc, Sbed, Skoll; CC BY 3.0.',
  },
  markdown: {
    resources: 'Счётчики',
    // Лист текстом (D54): подписи, которых нет на экране, потому что
    // на экране их заменяет вёрстка — заголовок таблицы, например.
    summary: 'Сводка',
    ability: 'Характеристика',
    score: 'Значение',
    mod: 'Модификатор',
    hit: 'Попадание',
    xp: 'Опыт',
    equipped: 'надето',
    coins: 'Монеты',
    weight: 'Вес',
    conditions: 'Состояния',
    spellLevel: '{{level}} круг',
    attribution:
      'Механика и тексты — SRD 5.2 (Wizards of the Coast), CC BY 4.0. Лист собран приложением Rollcall.',
  },
  custom: {
    // Свои записи контента (D53). Ветка называется custom, а не
    // content: под ключом content лежит словарь названий записей SRD,
    // и второй словарь поверх него молча съел бы первый (i18n.test.ts).
    title: 'Свои записи',
    hint: 'Записи, которых нет в SRD: своё оружие, подкласс из чужой книги, класс, придуманный за столом. Формат тот же, что у SRD, — значит, лист считает их наравне. К столу они подключаются флагом «Свой контент» в настройках кампании.',
    new: 'Завести запись',
    /** Как называть запись в отказе, когда поле не названо. */
    record: 'запись целиком',
    back: 'Назад',
    next: 'Дальше',
    save: 'Сохранить',
    cancel: 'Отменить',
    empty: 'Пока ничего не заведено.',
    edit: 'Править',
    remove: 'Удалить',
    removeUsed: 'Удалить ({{count}} на листах)',
    unused: 'на листах не используется',
    usedBy: 'персонажей с этой записью: {{count}}',
    usedWarning: 'Запись стоит на листах: {{count}}. Правка механики пересчитает их.',
    editorTitle: 'Запись контента',
    editorHint:
      'Шаги идут по порядку; назад можно вернуться, набранное не пропадёт. В кампанию запись попадает только после сохранения с последнего шага.',
    step: 'Шаг {{step}} из {{total}}',
    steps: {
      type: 'что заводим',
      base: 'с нуля или копией',
      fields: 'поля записи',
      effects: 'эффекты',
      preview: 'как это выглядит',
    },
    type: {
      item: 'Предмет',
      spell: 'Заклинание',
      subclass: 'Подкласс',
      species: 'Вид',
      background: 'Предыстория',
      feat: 'Черта',
      class: 'Класс',
    },
    fromScratch: 'С нуля',
    copyHint: 'Или взять существующую запись за основу — форма откроется заполненной.',
    searchLabel: 'Поиск по названию',
    own: 'своя',
    copy: 'Взять за основу',
    key: 'Ключ записи',
    keyHint:
      'Строчная латиница, цифры и подчёркивание. После создания ключ не меняется: на него ссылаются листы.',
    note: 'Откуда взято (заметка)',
    noEffects: 'Эффектов нет: запись ничего не считает.',
    effectsPreview: 'Что делает с листом',
    noName: 'Без названия',
    problemAt: '{{path}}: {{message}}',
    form: {
      unsupported:
        'Поле «{{field}}» редактор заполнить не умеет. Такое берётся копированием существующей записи.',
      variant: 'Вид',
      addItem: 'Добавить',
      removeItem: 'Убрать',
      present: 'заполнить',
      refHint: 'srd:ключ или u:владелец:ключ',
    },
    choice: {
      name: 'Как называется выбор',
      count: 'Сколько брать',
      from: 'Откуда варианты',
      list: 'Перечислить ссылками',
      options: 'Ссылки через запятую',
      add: 'Добавить выбор',
      remove: 'Убрать выбор',
      type: {
        skill: 'Любой навык',
        language: 'Любой язык',
        feat: 'Любая черта',
        spell: 'Любое заклинание',
        item: 'Любой предмет',
      },
    },
    effects: {
      target: 'Что меняем',
      opLabel: 'Операция',
      amount: 'Насколько',
      min: 'Не меньше',
      max: 'Не больше',
      cap: 'Предел вклада',
      kind: 'Число или величина',
      kindNumber: 'число',
      kindRef: 'величина листа',
      ref: 'Величина',
      trait: 'Признак',
      add: 'Добавить эффект',
      addTrait: 'Добавить признак',
      remove: 'Убрать',
      summary: 'Что получится',
      conflict:
        'На «{{target}}» уже есть «задать». Двух «задать» на одну величину не бывает: взаимоисключение задаётся условиями (D18.3).',
      limitsTitle: 'Чего здесь нельзя выразить',
      limits:
        'Перезарядка, легендарные действия, реакции и типы урона с сопротивлениями этим форматом не выражаются — ни для SRD, ни для своего. Такое пишется текстом в описании и отыгрывается за столом руками.',
      and: ' и ',
      opText: '{{target}}: {{op}} {{value}}',
      grantText: '{{target}}: {{value}}',
      cappedText: '{{amount}}, но не больше {{max}}',
      clampText: 'от {{min}} до {{max}}',
      condAdd: 'Добавить условие',
      condRemove: 'Убрать условие',
      condPath: 'Условие: что смотрим',
      condOp: 'Условие: как сравниваем',
      condValue: 'Условие: значение',
      self: 'эта запись (например, пока надета)',
      op: {
        set: 'задать',
        add: 'прибавить',
        add_capped: 'прибавить с пределом',
        mul: 'умножить на',
        clamp: 'ограничить',
      },
      condOpName: {
        eq: 'равно',
        neq: 'не равно',
        gte: 'не меньше',
        lte: 'не больше',
        has: 'есть',
        has_not: 'нет',
      },
      cond: {
        eq: '{{path}} = {{value}}',
        neq: '{{path}} ≠ {{value}}',
        gte: '{{path}} ≥ {{value}}',
        lte: '{{path}} ≤ {{value}}',
        in: '{{path}} среди: {{value}}',
        has: 'есть {{value}}',
        has_not: 'нет {{value}}',
      },
    },
    field: {
      name: 'Название',
      text: 'Описание',
      effects: 'Эффекты',
      mechanics: 'Механика',
      ability: 'Характеристика',
      abilityScores: 'Характеристики',
      acBonus: 'Прибавка к КД',
      amount: 'Сколько',
      armorTraining: 'Владение доспехами',
      attack: 'Атака',
      attackDamage: 'Урон атаки',
      attunement: 'Настройка',
      baseAc: 'Базовый КД',
      benefits: 'Способности',
      cantrips: 'Заговоры',
      carryingCapacityLb: 'Грузоподъёмность, фунты',
      castingTime: 'Время накладывания',
      category: 'Разряд',
      changePrepared: 'Смена подготовленных',
      choices: 'Выборы',
      choose: 'Сколько выбрать',
      classes: 'Классы',
      components: 'Компоненты',
      concentration: 'Сосредоточение',
      cost: 'Цена',
      creatureType: 'Тип существа',
      currency: 'Монета',
      damage: 'Урон',
      damageType: 'Тип урона',
      dexterityBonus: 'Прибавка Ловкости',
      duration: 'Длительность',
      equipment: 'Снаряжение',
      features: 'Особенности',
      from: 'Из чего',
      goldGp: 'Золото',
      hitDie: 'Кость хитов',
      id: 'Ключ',
      item: 'Позиция',
      items: 'Позиции',
      key: 'Ключ',
      kind: 'Вид',
      label: 'Подпись',
      level: 'Уровень',
      levels: 'Уровни',
      limit: 'Предел',
      mastery: 'Мастерство оружия',
      notation: 'Кости',
      onSuccess: 'При успехе',
      option: 'Вариант',
      options: 'Варианты',
      originFeat: 'Черта происхождения',
      pact: 'Договор',
      parentClass: 'Класс',
      prepared: 'Подготовка',
      prerequisite: 'Требование',
      primaryAbility: 'Главная характеристика',
      proficiencyBonus: 'Бонус мастерства',
      prompt: 'Вопрос',
      properties: 'Свойства',
      quantity: 'Количество',
      range: 'Дистанция',
      rarity: 'Редкость',
      reach: 'Досягаемость',
      ritual: 'Ритуал',
      save: 'Спасбросок',
      savingThrowProficiencies: 'Владение спасбросками',
      school: 'Школа',
      sizes: 'Размеры',
      skillProficiencies: 'Владение навыками',
      slots: 'Ячейки',
      speedFt: 'Скорость, футы',
      spellcasting: 'Заклинательство',
      startingEquipment: 'Стартовое снаряжение',
      stealthDisadvantage: 'Помеха на Скрытность',
      strengthRequirement: 'Требование к Силе',
      toolProficiencies: 'Владение инструментами',
      toolProficiency: 'Владение инструментом',
      traits: 'Черты',
      type: 'Вид',
      upgradeAt: 'Улучшается на уровне',
      value: 'Значение',
      weaponProficiencies: 'Владение оружием',
      weightLb: 'Вес, фунты',
      weight_class: 'Весовой класс',
      when: 'Когда',
    },
    option: {
      armor: 'доспех',
      shield: 'щит',
      shields: 'щиты',
      weapon: 'оружие',
      gear: 'снаряжение',
      tool: 'инструмент',
      magic: 'магический предмет',
      light: 'лёгкий',
      medium: 'средний',
      heavy: 'тяжёлый',
      melee: 'ближний бой',
      ranged: 'дальний бой',
      simple: 'простое',
      martial: 'воинское',
      bludgeoning: 'дробящий',
      piercing: 'колющий',
      slashing: 'рубящий',
      common: 'обычный',
      uncommon: 'необычный',
      rare: 'редкий',
      very_rare: 'очень редкий',
      legendary: 'легендарный',
      varies: 'по-разному',
      none: 'нет',
      cp: 'медь',
      sp: 'серебро',
      gp: 'золото',
      abjuration: 'ограждение',
      conjuration: 'вызов',
      divination: 'прорицание',
      enchantment: 'очарование',
      evocation: 'воплощение',
      illusion: 'иллюзия',
      necromancy: 'некромантия',
      transmutation: 'преобразование',
      strength: 'Сила',
      dexterity: 'Ловкость',
      constitution: 'Телосложение',
      intelligence: 'Интеллект',
      wisdom: 'Мудрость',
      charisma: 'Харизма',
      small: 'маленький',
      large: 'большой',
      origin: 'предыстории',
      general: 'общая',
      fighting_style: 'боевой стиль',
      epic_boon: 'эпический дар',
      full: 'полное',
      catalogue: 'из справочника',
      feat: 'черта',
      item: 'предмет',
      language: 'язык',
      level: 'уровень',
      list: 'список',
      long_rest: 'долгий отдых',
      max_2: 'не выше 2',
      skill: 'навык',
      spell: 'заклинание',
    },
    path: {
      ac: 'КД',
      initiative: 'Инициатива',
      level: 'Уровень',
      languages: 'Языки',
      proficiencies: 'Владения',
      proficiency_bonus: 'Бонус мастерства',
      resistances: 'Сопротивления',
      advantages: 'Преимущества',
      disadvantages: 'Помехи',
      worn: 'Надетое',
      speed: 'Скорость',
      'hp.max': 'Максимум хитов',
      'senses.darkvision': 'Тёмное зрение',
      'speeds.swim': 'Скорость плавания',
      'speeds.fly': 'Скорость полёта',
      'speeds.climb': 'Скорость лазания',
      'resources.uses': 'Использования',
      'passive.perception': 'Пассивное Восприятие',
      'spell.attack': 'Атака заклинанием',
      'spell.save_dc': 'Сложность заклинаний',
      'saves.str': 'Спасбросок Силы',
      'saves.dex': 'Спасбросок Ловкости',
      'saves.con': 'Спасбросок Телосложения',
      'saves.int': 'Спасбросок Интеллекта',
      'saves.wis': 'Спасбросок Мудрости',
      'saves.cha': 'Спасбросок Харизмы',
      'check.str': 'Проверка Силы',
      'check.dex': 'Проверка Ловкости',
      'check.con': 'Проверка Телосложения',
      'check.int': 'Проверка Интеллекта',
      'check.wis': 'Проверка Мудрости',
      'check.cha': 'Проверка Харизмы',
      'abilities.str.score': 'Сила',
      'abilities.dex.score': 'Ловкость',
      'abilities.con.score': 'Телосложение',
      'abilities.int.score': 'Интеллект',
      'abilities.wis.score': 'Мудрость',
      'abilities.cha.score': 'Харизма',
      'abilities.str.mod': 'Модификатор Силы',
      'abilities.dex.mod': 'Модификатор Ловкости',
      'abilities.con.mod': 'Модификатор Телосложения',
      'abilities.int.mod': 'Модификатор Интеллекта',
      'abilities.wis.mod': 'Модификатор Мудрости',
      'abilities.cha.mod': 'Модификатор Харизмы',
      'carry.weightLb': 'Вес при себе',
      'carry.capacityLb': 'Предел переносимого',
      'carry.over': 'Перегруз',
      'state.equipped': 'Надетое',
      'state.concentration': 'Сосредоточение',
      'state.activeEffects': 'Активные эффекты',
    },
  },
  about: {
    version: 'Версия {{version}} · сборка {{commit}}',
    // Указание источника — условие лицензии CC BY 4.0 (D7): подпись
    // объясняет, что это, а дословный текст стоит в подсказке
    // и по ссылкам рядом.
    srd: 'Материалы SRD 5.2, Wizards of the Coast:',
    title: 'О приложении',
    srdTitle: 'Источник материалов',
    // Возврат туда, откуда вошли: с листа — на лист, из кампании —
    // в кампанию. Поэтому подпись без имени экрана.
    back: 'Назад',
  },
  app: {
    // Подпись набора вкладок листа: они принадлежат листу,
    // и на других экранах её нет.
    title: 'Лист персонажа',
    // Заголовок шапки там, где экран не назвался сам: незнакомый
    // адрес. Имени продукта нет (open-questions, п. 1), поэтому
    // здесь описание, а не название.
    name: 'Лист персонажа и кампания',
    // Знак вопроса у заголовка раздела: подпись для голосового
    // доступа, на экране виден только сам знак.
    hint: 'Как устроен раздел «{{section}}»',
  },
  theme: {
    title: 'Тема',
    toggle: 'Сменить тему',
    light: 'Светлая тема',
    dark: 'Тёмная тема',
  },
  // Меню шапки (D10): всё, что раньше стояло в ней кнопками.
  // Главная аккаунта (D8): персонажи и кампании рядом. Заголовок
  // называет то, что под ним, — и то и другое сразу.
  home: {
    title: 'Мои игры',
  },
  menu: {
    title: 'Меню',
    close: 'Закрыть меню',
    home: 'На главную',
  },
  // Переключатель языка в шапке (D11). Названия языков — каждое
  // на своём языке: «English» узнаётся и тем, кто русского
  // не читает, а «Английский» — нет.
  language: {
    title: 'Язык',
    toggle: 'Сменить язык',
    ru: 'Русский',
    en: 'English',
    // Код на кнопке: он одинаков в обоих словарях и всё равно живёт
    // здесь — это видимый текст, а видимый текст в компоненте
    // означает строку мимо словаря (D11).
    codeRu: 'РУ',
    codeEn: 'EN',
  },
  tabs: {
    combat: 'Бой',
    spells: 'Заклинания',
    inventory: 'Инвентарь',
    character: 'Персонаж',
    empty: 'Появится в следующем заходе.',
  },
  // Счётчики ресурсов (D56): ярости, вдохновение, Божественный канал.
  resources: {
    count: '{{current}} из {{max}}',
    spend: 'Потратить',
    restore: 'Вернуть',
    own: 'Свои счётчики',
    addOpen: 'Завести счётчик',
    add: 'Завести',
    remove: 'Убрать',
    name: 'Название',
    max: 'Сколько всего',
    recoveryLabel: 'Чем восстанавливается',
    maxNumber: 'числом',
    maxValue: 'Сколько именно',
    min: 'Не меньше чем',
    cancel: 'Отмена',
    recovery: {
      none: 'отдыхом не восстанавливается',
      long: 'возвращается длинным отдыхом',
      short: 'возвращается коротким отдыхом',
      shortSome: 'короткий отдых возвращает {{uses}}, длинный — всё',
    },
  },
  // Имя и описание персонажа (D4).
  details: {
    title: 'О персонаже',
    empty: 'Про персонажа пока ничего не записано.',
    edit: 'Править',
    save: 'Сохранить',
    name: 'Имя',
    appearance: 'Внешность',
    quirk: 'Фишка',
    motivation: 'Причина',
    backstory: 'Прошлое',
    weakness: 'Слабость',
    notes: 'Заметки',
  },
  sheet: {
    level: 'Уровень',
    levelShort: 'ур. {{level}}',
    ac: 'Класс доспеха',
    hpMax: 'Максимум хитов',
    initiative: 'Инициатива',
    speed: 'Скорость',
    proficiencyBonus: 'Бонус мастерства',
    darkvision: 'Тёмное зрение',
    passivePerception: 'Пассивное Восприятие',
    uses: 'Использования',
    climb: 'Скорость лазания',
    fly: 'Скорость полёта',
    swim: 'Скорость плавания',
    abilities: 'Характеристики',
    modifier: 'модификатор {{value}}',
    score: 'значение {{value}}',
    saves: 'Спасброски',
    skills: 'Навыки',
    proficient: 'владение',
    proficientIn: '{{ability}} · владение',
    traits: 'Владения и признаки',
    proficiencies: 'Владения',
    languages: 'Языки',
    resistances: 'Сопротивления',
    advantages: 'Преимущества',
    disadvantages: 'Помехи',
    features: 'Черты и особенности',
    rest: 'Прочие величины',
    draft: 'Драфт: персонаж ни в какой кампании',
    code: 'Код персонажа',
    copyCode: 'Скопировать код',
    codeCopied: 'Код скопирован',
    codeHint:
      'Код нужен, если садиться за стол вы будете с другого устройства: введите его на экране приглашения (D55).',
    abilityCheck: 'Проверка: {{name}}',
    hpLevel: 'Хиты за {{level}} уровень',
    traitKind: {
      save: 'Спасброски',
      skill: 'Навыки',
      armor: 'Доспехи',
      weapon: 'Оружие',
      tool: 'Инструменты',
      check: 'Проверки',
      other: 'Прочее',
    },
  },
  inventory: {
    title: 'Инвентарь',
    empty: 'Предметов нет.',
    equipped: 'Надето',
    carried: 'В сумке',
    coins: 'Монеты',
    coinsValue: '{{count}} зм',
    quantity: '{{count}} шт.',
    weight: '{{count}} фн',
    weightTotal: 'Вес',
    weightOf: '{{total}} фн из {{carry}} фн',
    carryHint: 'Больше {{carry}} фн — перегруз: скорость падает до пяти футов.',
    over: 'Перегруз: вес больше {{carry}} фн, скорость на листе — {{speed}} фт.',
    dragHint: 'Тащить, волочить и поднимать можно до {{drag}} фн.',
    unknownWeight: 'Вес не посчитан: {{items}}. У этих позиций он зависит от варианта.',
    noRecord: 'Записи в контенте нет: с позицией ничего не сделать.',
    equip: 'Надеть',
    unequip: 'Снять',
    drop: 'Выбросить',
    count: 'Количество',
    setCount: 'Изменить',
    busy: 'Отправляем…',
    refusal: {
      'unknown-item': 'Такого предмета в контенте нет.',
      'not-in-inventory': 'Этого предмета у персонажа нет.',
      'not-a-record': 'Это не предмет снаряжения.',
      'already-equipped': 'Уже надето.',
      'not-equipped': 'Не надето.',
      'not-equippable': 'Это не надевают.',
      'armor-already-worn': 'Доспех уже надет: {{blockedBy}}. Сначала снимите его.',
      'shield-already-held': 'Щит уже в руке: {{blockedBy}}. Рука одна.',
      'bad-count': 'Такое количество не подходит.',
    },
    less: '{{name}}: на одну меньше',
    more: '{{name}}: на одну больше',
    details: 'Описание',
    dropConfirm: 'Выбросить «{{name}}»? Сейчас {{count}} шт.',
    dropOne: 'Выбросить одну',
    dropAll: 'Выбросить всё',
    cancel: 'Отмена',
  },
  combat: {
    title: 'Бой',
    hp: 'Хиты',
    hpOf: '{{current}} из {{max}}',
    temp: 'Временные хиты',
    tempValue: '+{{count}} временных',
    hitDie: 'Кость хитов',
    hitDiceValue: '{{count}} × {{die}}',
    attacks: 'Атаки',
    noAttacks: 'Оружия нет: возьмите его во вкладке «Инвентарь».',
    inHands: 'В руках',
    carried: 'В сумке',
    attack: 'Атака',
    damage: 'Урон',
    versatile: 'двумя руками {{notation}}',
    proficient: 'владение',
    noProficiency: 'без владения',
    spellAttack: 'заговор',
    ranged: 'дальний бой',
    melee: 'ближний бой',
    rollAttack: 'Бросок атаки',
    rollDamage: 'Бросок урона',
    change: 'Изменить хиты',
    damageAction: 'Урон',
    healAction: 'Лечение',
    tempAction: 'Временные',
    amount: 'Сколько',
    rest: 'Долгий отдых',
    shortRest: 'Короткий отдых',
    spendDie: 'Потратить кость хитов ({{left}} × {{die}})',
    hitDiceSpent: 'потрачено {{count}}',
    restHint: 'Возвращает хиты и ячейки, снимает сосредоточение.',
    refusal: {
      'bad-amount': 'Число хитов — целое и неотрицательное.',
      'already-full': 'Хиты и так на максимуме.',
      'already-down': 'Хитов и так ноль.',
      'no-hit-dice': 'Кости хитов кончились: их вернёт долгий отдых.',
      'no-hit-die': 'У персонажа нет кости хитов: класс не выбран.',
    },
    attackOf: 'Атака: {{name}}',
    damageOf: 'Урон: {{name}}',
    shortRestHint: 'Сам по себе не лечит: хиты возвращает потраченная кость.',
    spendDieHint: 'Бросок {{die}} плюс Телосложение, минимум 1 хит.',
    spendDieFull: 'Пока хиты полны, тратить кость незачем.',
    spendDieNone: 'Костей хитов не осталось — до длинного отдыха.',
    restedLong: 'Долгий отдых: хиты, кости хитов и ячейки восстановлены.',
    restedShort:
      'Короткий отдых. Костей хитов осталось: {{left}}. Лечит потраченная кость.',
  },
  spells: {
    title: 'Заклинания',
    none: 'Класс {{class}} не колдует: заклинаний у него нет.',
    noneNoClass: 'Класс не выбран, поэтому заклинаний нет.',
    numbers: 'Числа заклинателя',
    abilityIs: 'Характеристика заклинаний: {{ability}}',
    saveDc: 'Сложность спасброска',
    attack: 'Бонус атаки заклинаниями',
    slots: 'Ячейки',
    slotLevel: '{{level}} круг',
    slotsOf: '{{left}} из {{total}}',
    spend: 'Потратить',
    restore: 'Вернуть',
    cantrips: 'Заговоры',
    prepared: 'Подготовленные',
    counts:
      'Заговоров {{cantrips}} из {{cantripsMax}}, заклинаний {{prepared}} из {{preparedMax}}',
    filter: 'Круг',
    filterAll: 'Все',
    cantripShort: 'заговор',
    levelShort: '{{level}} круг',
    add: 'Подготовить',
    remove: 'Убрать',
    available: 'Список класса',
    availableHint: 'Из этого готовят заклинания на день.',
    availableCount:
      'В списке класса {{count}} заклинаний. Выберите круг выше, чтобы посмотреть их.',
    loading: 'Загрузка списка…',
    details: 'Подробно',
    schoolOf: {
      abjuration: 'Ограждение',
      conjuration: 'Вызов',
      divination: 'Прорицание',
      enchantment: 'Очарование',
      evocation: 'Воплощение',
      illusion: 'Иллюзия',
      necromancy: 'Некромантия',
      transmutation: 'Преобразование',
    },
    labelOf: {
      'Prepared Spells': 'Подготовленные заклинания',
      'Spells Known': 'Известные заклинания',
    },
    // Параметры заклинания собираются из этих кусков (`spell-facts.ts`):
    // в источнике это одна строка на поле, а на экране — русская
    // шапка карточки.
    fact: {
      action: 'Действие',
      bonusAction: 'Бонусное действие',
      reaction: 'Реакция',
      orRitual: '{{time}} или ритуал',
      withTail: '{{head}}, {{tail}}',
      self: 'На себя',
      touch: 'Касание',
      sight: 'В пределах видимости',
      unlimited: 'Неограниченная',
      special: 'Особая',
      feet: '{{count}} футов',
      instantaneous: 'Мгновенная',
      dispelled: 'Пока не рассеют',
      dispelledOrTriggered: 'Пока не рассеют или не сработает',
      concentration: 'Сосредоточение, до {{amount}}',
      upTo: 'До {{amount}}',
      component: { v: 'В', s: 'С', m: 'М' },
      material: '{{letters}} ({{text}})',
    },
    // Единицы времени и расстояния в двух формах: обычной («8 часов»)
    // и после «до» («до 8 часов»). Родительный падеж — не украшение:
    // «Сосредоточение, до 1 минута» читается как ошибка перевода.
    unit: {
      round_one: '{{count}} раунд',
      round_few: '{{count}} раунда',
      round_many: '{{count}} раундов',
      round_other: '{{count}} раунда',
      minute_one: '{{count}} минута',
      minute_few: '{{count}} минуты',
      minute_many: '{{count}} минут',
      minute_other: '{{count}} минуты',
      hour_one: '{{count}} час',
      hour_few: '{{count}} часа',
      hour_many: '{{count}} часов',
      hour_other: '{{count}} часа',
      day_one: '{{count}} день',
      day_few: '{{count}} дня',
      day_many: '{{count}} дней',
      day_other: '{{count}} дня',
      mile_one: '{{count}} миля',
      mile_few: '{{count}} мили',
      mile_many: '{{count}} миль',
      mile_other: '{{count}} мили',
    },
    unitOf: {
      round_one: '{{count}} раунда',
      round_few: '{{count}} раундов',
      round_many: '{{count}} раундов',
      round_other: '{{count}} раундов',
      minute_one: '{{count}} минуты',
      minute_few: '{{count}} минут',
      minute_many: '{{count}} минут',
      minute_other: '{{count}} минут',
      hour_one: '{{count}} часа',
      hour_few: '{{count}} часов',
      hour_many: '{{count}} часов',
      hour_other: '{{count}} часов',
      day_one: '{{count}} дня',
      day_few: '{{count}} дней',
      day_many: '{{count}} дней',
      day_other: '{{count}} дней',
      mile_one: '{{count}} мили',
      mile_few: '{{count}} миль',
      mile_many: '{{count}} миль',
      mile_other: '{{count}} миль',
    },
    school: 'Школа',
    castingTime: 'Время',
    range: 'Дистанция',
    components: 'Компоненты',
    duration: 'Длительность',
    concentration: 'Сосредоточение',
    ritual: 'Ритуал',
    concentrateOn: 'Сосредоточиться',
    concentrationDrop: 'Снять сосредоточение',
    concentrationNow: 'Сейчас: {{spell}}',
    concentrationNone: 'Ни на чём не сосредоточен',
    concentrationReplaces: 'Снимет прежнее: {{spell}}',
    refusal: {
      'unknown-spell': 'Такого заклинания в контенте нет.',
      'not-a-spell': 'Это не заклинание.',
      'not-in-class-list': 'Этого заклинания нет в списке класса.',
      'already-prepared': 'Уже подготовлено.',
      'not-prepared': 'Не подготовлено.',
      'no-room': 'Больше не помещается: столько положено по таблице класса.',
      'no-such-slot': 'Ячеек этого круга у персонажа нет.',
      'no-slots-left': 'Ячейки этого круга кончились.',
      'nothing-to-restore': 'Ячейки этого круга и так целы.',
      'not-a-caster': 'Класс не колдует.',
      'not-concentration': 'Это заклинание не требует сосредоточения.',
      'not-now': 'Сейчас набор не меняют: класс меняет его отдыхом или уровнем.',
      'change-limit': 'За раз меняется одно заклинание, и оно уже поменяно.',
      'cantrip-fixed': 'Заговоры меняются при получении уровня класса.',
    },
    changeHintRest: 'Набор подготовленных меняют по окончании длинного отдыха.',
    changeHintLevel: 'Набор подготовленных меняют при получении уровня класса.',
    circle: '{{level}} круг',
  },
  // Не внутри `content`: там лежит словарь названий записей SRD
  // (`termsFor`), и он затирает всё, что положат рядом. Так и вышло —
  // кнопка показывала «content.original» вместо «Оригинал».
  srd: {
    original: 'Оригинал',
    originalHint: 'Перевод наш; SRD 5.2 — Wizards of the Coast, CC BY 4.0.',
  },
  terms: {
    hint: 'Что такое «{{name}}»',
    // Редакция названа прямо в ссылке: на dnd.su нет материала
    // 2024 года, а наш контент — SRD 5.2. Умолчать об этом значило бы
    // отправить человека читать про другого персонажа (D57).
    dndSu: 'Подробнее на dnd.su (редакция 2014 года)',
  },
  roll: {
    title: 'Броски',
    empty: 'Бросков ещё не было.',
    roll: 'Бросить',
    rollValue: 'Бросить: {{label}}',
    because: 'по признаку: {{trait}}',
    manual: 'Внести свой',
    manualHint: 'Бросили настоящие кости — впишите результат.',
    manualMark: 'вручную',
    result: 'Итог {{total}}',
    dice: 'Кости: {{values}}',
    dropped: '({{value}})',
    modifiers: 'Прибавки: {{parts}}',
    notation: 'Запись броска',
    notationHint: 'Например, 2d6+3.',
    mode: 'Как кидаем',
    normal: 'Обычно',
    advantage: 'С преимуществом',
    disadvantage: 'С помехой',
    reason: {
      attack: 'Атака: {{subject}}',
      damage: 'Урон: {{subject}}',
      check: 'Проверка: {{subject}}',
      save: 'Спасбросок: {{subject}}',
      initiative: 'Инициатива',
      ability_pool: 'Характеристики',
      hit_die: 'Кость хитов',
      level_hp: 'Хиты за уровень',
      custom: 'Свой бросок',
    },
    source: {
      notation: 'из записи',
    },
    hidden: 'Скрытый бросок',
    hiddenLabel: 'Бросить скрыто',
    hiddenHint:
      'Результат увидите только вы. В общем журнале останется отметка, что бросок был.',
    refusal: 'Бросок не получился: проверьте запись.',
    // Только на одиночной d20: с преимуществом костей две, и выделять
    // там нечего.
    critical: 'Критический успех',
    fumble: 'Критический провал',
    panel: 'Броски',
    open: 'Бросить',
    close: 'Закрыть панель',
    last: 'Последний бросок',
    by: 'По чему бросить',
    // Тире, а не двоеточие: в подписи уже есть одно —
    // «Проверка: Уход за животными».
    again: 'Бросить ещё раз — {{subject}}',
    modeHint: 'Действует на все броски панели и на атаки во вкладке «Бой».',
    manualAdd: 'Записать свой результат',
  },
  level: {
    title: 'Повышение уровня',
    upTo: 'Повышение до {{level}} уровня',
    buildLeft_one:
      'Собираем персонажа до {{target}} уровня: остался {{count}} уровень. Прервать можно в любой момент — персонаж останется на достигнутом.',
    buildLeft_few:
      'Собираем персонажа до {{target}} уровня: осталось {{count}} уровня. Прервать можно в любой момент — персонаж останется на достигнутом.',
    buildLeft_many:
      'Собираем персонажа до {{target}} уровня: осталось {{count}} уровней. Прервать можно в любой момент — персонаж останется на достигнутом.',
    building_one: 'Уровень {{level}}. Остался ещё {{count}}.',
    building_few: 'Уровень {{level}}. Осталось ещё {{count}}.',
    building_many: 'Уровень {{level}}. Осталось ещё {{count}}.',
    open: 'Поднять уровень',
    stopBuild: 'Прервать сборку',
    features: 'Что приходит с уровнем',
    noFeatures: 'На этом уровне новых особенностей нет.',
    hitPoints: 'Хиты за уровень',
    hitPointsHint:
      'Кость хитов {{die}} плюс Телосложение, либо фиксированное значение {{fixed}}.',
    hpFixed: 'Взять {{value}}',
    hpRoll: 'Бросить {{die}}',
    confirm: 'Поднять до {{level}}',
    undo: 'Откатить {{level}} уровень',
    back: 'Назад к листу',
    noPlan: 'Поднимать некуда: у класса нет следующего уровня.',
    current: 'Уровень {{level}}',
    done: 'Уровень поднят: теперь {{level}}.',
    undone: 'Повышение откачено: снова {{level}} уровень.',
    hpManual: 'Бросил сам',
    hpManualField: 'Сколько выпало',
    hpManualHint: 'Число на настоящей кости {{die}} — без прибавки Телосложения.',
    incomplete: 'Сначала сделайте выборы уровня.',
  },
  provenance: {
    title: 'Из чего сложилось',
    close: 'Закрыть',
    explain: '{{label}}: {{value}}. Показать разбор',
    journal: 'Выбор при создании',
    rulesetOther: 'Правила системы',
    ruleset: {
      character_creation: 'Базовые правила',
      proficiency_bonus: 'Бонус мастерства',
      spell_save_dc: 'Основа сложности заклинаний',
    },
    op: {
      set: 'основа {{value}}',
      add: '{{value}}',
      mul: '× {{value}}',
      addCapped: '{{value}}, но не больше {{max}}',
      clamp: 'не ниже {{min}}, не выше {{max}}',
      grant: 'признак «{{value}}»',
    },
  },
  characters: {
    title: 'Персонажи',
    empty: 'Персонажей пока нет.',
    create: 'Создать персонажа',
    open: 'Открыть',
    loading: 'Загрузка…',
    level: 'Уровень {{level}}',
    // «К списку» не говорило, к какому: на листе рядом стоят список
    // заклинаний, список предметов и журнал бросков.
    back: 'К списку персонажей',
    draft: 'Без кампании',
    // Почему персонаж не в кампании, хотя аккаунт — мастерский.
    draftHint:
      'Персонаж без кампании считается и растёт как обычный — ему не хватает только стола. За стол он попадает по ссылке на место, а не тем, что лежит в аккаунте.',
    inCampaign: 'В кампании',
    missing:
      'Такого персонажа нет. Персонаж, созданный без кампании, открывается только в том браузере, где его создали, — и только пока вы не перенесли его в аккаунт.',
  },
  /**
   * Панель мастера (D3, D10). Мастер отдаёт команды, а не правит
   * числа, и подписи кнопок говорят именно это: «снять хиты»,
   * а не «поставить хиты».
   */
  master: {
    party: 'Партия',
    empty:
      'За столом пока никого. Отправьте игрокам ссылку кампании — каждый появится здесь сам, как только вы подтвердите его заявку.',
    commands: 'Команды',
    hide: 'Свернуть',
    failed: 'Команда не прошла.',
    level: 'уровень {{level}}',
    ac: 'КД',
    passive: 'Пассивное Восприятие',
    hp: {
      title: 'Хиты',
      amount: 'Сколько',
      damage: 'Снять хиты',
      heal: 'Вылечить',
      temp: 'Временные',
    },
    item: {
      title: 'Предмет',
      choose: 'Выберите предмет',
      quantity: 'Штук',
      give: 'Выдать',
      take: 'Забрать',
    },
    condition: {
      title: 'Состояние',
      choose: 'Выберите состояние',
      apply: 'Повесить',
      raise: 'Ещё уровень',
      remove: 'Снять',
      none: 'Состояний нет',
    },
    xp: {
      award: 'Опыт',
      give: 'Начислить',
      total: 'Опыт: {{total}}',
      next: 'до {{level}} уровня — {{at}}',
      ready: 'Порог взят: доступно повышение',
      self: 'Начислить себе',
      selfHint:
        'Этот персонаж не за столом, и начислить ему опыт больше некому. Как только он сядет в кампанию, опытом займётся мастер.',
    },
    rest: {
      short: 'Короткий отдых',
      long: 'Долгий отдых',
      hint: 'Отдохнут все сразу: хиты, кости хитов и ячейки восстановятся у каждого за столом. Отдельно за одного персонажа отдых не объявляют.',
    },
    monsters: {
      // Подпись короткая намеренно: она стоит в строке из четырёх
      // ячеек внутри колонки, и «Поиск монстра» ломалось на две
      // строки, задирая всю строку формы.
      search: 'Поиск',
      pick: 'Монстр',
      none: 'не выбран',
      option_one: '{{name}} · опасность {{challenge}} · {{count}} хит · КД {{ac}}',
      option_few: '{{name}} · опасность {{challenge}} · {{count}} хита · КД {{ac}}',
      option_many: '{{name}} · опасность {{challenge}} · {{count}} хитов · КД {{ac}}',
      option_other: '{{name}} · опасность {{challenge}} · {{count}} хита · КД {{ac}}',
      count: 'Сколько',
      add: 'Добавить в очередь',
      hp: '{{current}} из {{max}} хитов',
      ac: 'КД {{ac, number}}',
      down: 'повержен',
      amount: 'Сколько',
      damage: 'Снять',
      heal: 'Вернуть',
      hidden: 'скрытый бросок',
      attack: '{{name}} {{bonus}} · {{damage}}',
    },
    combat: {
      title: 'Очередь боя',
      idle: 'Бой не идёт',
      round: 'Круг {{round}}',
      empty: 'Очередь пуста.',
      startRolled: 'Начать бой, бросить за всех',
      startEmpty: 'Начать бой, каждый бросает сам',
      next: 'Следующий ход',
      end: 'Закончить бой',
      now: 'ходит',
      roll: 'Бросить',
      remove: 'Убрать',
      creature: 'Существо',
      add: 'Добавить строку',
    },
    rolls: {
      title: 'Броски за столом',
      empty: 'Бросков пока не было.',
      hidden: 'бросок скрыт',
      secret: 'скрытый',
    },
  },

  campaigns: {
    review: {
      title: 'Что не сходится с кампанией',
      level: {
        what: 'Уровень персонажа {{character}}, кампания стартует с {{campaign}}.',
        riskAbove:
          'За столом он будет сильнее остальных: больше хитов, больше ячеек, лишние особенности.',
        riskBelow:
          'За столом он будет слабее остальных и быстрее выйдет из строя в первом же бою.',
        waysAbove:
          'Принять как есть, вернуть на переделку или поднять уровень кампании. Понизить персонажа нельзя: это отменило бы выборы игрока.',
        waysBelow:
          'Принять как есть — игрок доберёт уровни обычным повышением — или вернуть на переделку.',
      },
      missing: {
        what: 'Персонаж ссылается на записи, которых нигде нет: {{names}}.',
        risk: 'Лист такого персонажа не соберётся: расчёт упрётся в ссылку, по которой ничего нет.',
        ways: 'Вернуть на переделку. Принимать нечего — показывать будет нечего.',
      },
      unapproved: {
        what: 'Свои записи игрока, которых кампания не подтверждала: {{names}}.',
        risk: 'До вашего решения за столом они не работают: кампания их не видит, и числа у игрока разошлись бы с вашими.',
        ways: 'Принять — записи перейдут столу и заработают у этого персонажа — или вернуть на переделку с объяснением.',
      },
      disabled: {
        what: 'Персонаж опирается на свой контент, а в кампании он выключен: {{names}}.',
        risk: 'Флаг закрывает свой контент для создания, а решение здесь за вами: примете — записи перейдут столу и заработают.',
        ways: 'Принять, если записи вас устраивают, или вернуть на переделку с объяснением.',
      },
      raise: 'Поднять уровень кампании до {{level}}',
    },
    title: 'Мои кампании',
    empty: 'Кампаний пока нет. Заведите первую — места и ссылки появятся внутри.',
    create: 'Завести кампанию',
    name: 'Название',
    customContent: 'Использовать пользовательский контент',
    customContentHint:
      'Ваши записи будут предлагаться при создании персонажа наравне с книжными. Завести их можно в «Своих записях» — они принадлежат вам, а не кампании, и к столу подключаются этим флажком.',
    customContentOpen: 'Свои записи',
    ruleset: 'Система правил',
    'ruleset.dnd5e-2024': 'D&D 5e, редакция 2024',
    submit: 'Создать',
    cancel: 'Отмена',
    open: 'Открыть',
    archive: 'В архив',
    unarchive: 'Вернуть в работу',
    archived: 'В архиве',
    showArchived: 'Показать архивные',
    hideArchived: 'Скрыть архивные',
    // Удаления нет намеренно: в кампании живут чужие персонажи (D37).
    noDelete:
      'Кампанию можно убрать в архив: из списка она пропадёт, а всё, что было за столом, останется. Удаления нет — вместе с кампанией исчезли бы персонажи игроков.',
    summary: 'За столом {{used}} из {{limit}}',
    pending: 'Ждут решения: {{count}}',
    rename: 'Переименовать',
    save: 'Сохранить',
    // Признак связи (D2): без него «ничего не происходит»
    // не отличить от «оборвалось».
    stream: {
      live: 'Связь есть: новое появится само',
      connecting: 'Связи нет, восстанавливаем…',
      offline: 'Связи нет. Обновите страницу',
    },
    // Одна ссылка на кампанию (D51): мастер бросает её в общий чат,
    // а места заранее не заводит.
    invite: {
      title: 'Ссылка и настройки',
      // Раздел свёрнут: к нему возвращаются раз в кампанию (D52).
      show: 'Показать',
      hide: 'Скрыть',
      about: 'Как это устроено',
      hint: 'Ссылка одна на всю кампанию и работает, пока вы её не отзовёте. Её можно отправить в общий чат: перейти по ней сможет любой, но за стол пускаете вы — каждая заявка ждёт вашего подтверждения.',
      create: 'Сделать ссылку',
      copy: 'Скопировать',
      copied: 'Скопировано',
      revoke: 'Отозвать ссылку',
      limit: 'Сколько человек ждёте',
      limitHint:
        'Это предел, а не список: мест заранее нет, они появляются, когда вы подтверждаете заявку. Когда предел кончится, новые заявки будут отклоняться сами — вы это увидите и сможете поднять число.',
      startLevel: 'Стартовый уровень',
      startLevelHint:
        'С какого уровня приходят новые игроки. Персонаж всё равно собирается по шагам: создание, потом повышения. Уже сидящих за столом это не трогает.',
      startLevelSave: 'Сохранить уровень',
      limitSave: 'Сохранить',
      used: 'Занято {{used}} из {{limit}}',
    },
    seats: {
      title: 'За столом',
      empty: 'Мест пока нет. Заведите столько, сколько ждёте игроков.',
      // Мест заранее больше нет (D51): пустой список — это «никого
      // ещё не пустили», а не «вы забыли что-то настроить».
      emptyNow:
        'За столом пока никого. Отправьте ссылку — и подтвердите первую заявку.',
      add: 'Добавить место',
      label: 'Подпись места',
      free: 'Свободно',
      pending: 'Ждёт подтверждения',
      taken: 'Занято',
      invite: 'Выпустить ссылку',
      reissue: 'Выпустить новую',
      revoke: 'Отозвать ссылку',
      inviteReady: 'Ссылка выпущена. Скопируйте её сейчас — второй раз её не показать.',
      copy: 'Скопировать',
      copied: 'Скопировано',
      expires: 'Действует до {{at}}',
      release: 'Освободить место',
      // Освобождение возвращает персонажа игроку, а не удаляет (D37).
      // Подсказка говорит, что случится, а не как это устроено:
      // «снова станет драфтом» — слово из нашей головы, а не из
      // головы мастера, который просто пересаживает игрока.
      releaseHint:
        'Игрок уйдёт из партии, а его персонаж останется при нём — со всей историей и вещами. Ничего не пропадёт: удалять чужих персонажей приложение не умеет.',
      released: 'Место свободно, персонаж вернулся игроку',
      noCharacter: 'Персонажа пока нет',
      // Перенос драфта на аккаунт (D33) и посадка за место (D29) —
      // разные действия, и путать их естественно: и то и другое
      // выглядит как «мой персонаж теперь тут».
      noCharacterHint:
        'Персонажа приносит игрок: он открывает вашу ссылку и либо создаёт нового, либо берёт готового из своих. Свои персонажи в аккаунте за столом сами не появятся — их тоже нужно привести по ссылке.',
    },
    requests: {
      title: 'Заявки',
      empty: 'Заявок нет.',
      // Ссылку пересылают дальше без ведома мастера (D51), и очередь
      // остаётся единственным местом, где решают, кто сядет за стол.
      hint: 'Ссылку могли переслать дальше, поэтому смотрите, кого пускаете: имя, вид, класс и уровень — то, по чему свой игрок отличается от случайного человека.',
      first_login: 'Первый вход',
      device_change: 'Смена устройства',
      edit: 'Правка выборов',
      approveEdit: 'Принять правку',
      note: 'Что сказать игроку, если откажете',
      approve: 'Пустить',
      reject: 'Отклонить',
      unknown: 'Без имени',
      level: 'Уровень {{level}}',
      origin: {
        new: 'Новый персонаж',
        draft: 'Готовый персонаж',
      },
      autoRejected: 'Отклонена: мест не осталось',
      autoRejectedHint:
        'Поднимите число мест выше — и человек подаст заявку снова по той же ссылке.',
    },
  },

  join: {
    title: 'Приглашение',
    working: 'Проверяем ссылку…',
    // До подтверждения мастером состав партии закрыт (D38); название
    // кампании и число свободных мест видно сразу (D51).
    pending:
      'Заявка принята. Мастер её увидит; пока он не подтвердил, о партии ничего не известно.',
    // Мест не осталось: отказ, а не ошибка — по ссылке мог перейти
    // человек, которому её просто переслали (D51).
    rejected: {
      seat_limit:
        'Мест за этим столом сейчас нет. Заявку мастер увидит: если он добавит место, вы сможете подать её снова по той же ссылке.',
      byGm: 'Мастер отклонил заявку.',
    },
    rejectedHint:
      'Это не ошибка и ничего сломанного здесь нет. Если ссылку прислал ваш мастер — напишите ему; если её переслали, вы, возможно, попали не за тот стол.',
    refused: 'Ссылка не действует: она отозвана или набрана с ошибкой.',
    recheck: 'Проверить снова',
    seatsLeft: 'Свободных мест: {{left}}',
    full: 'Свободных мест сейчас нет. Заявку подать можно — мастер увидит, что кто-то не поместился.',
    pickCharacter: 'С кем вы идёте',
    pickCharacterHint:
      'Возьмите готового персонажа или создайте нового. Он останется вашим: если мастер откажет или потом освободит место, персонаж вернётся к вам целиком.',
    mySeats: 'Мои места',
    seatOf: '{{campaign}} · {{seat}}',
    archived: 'Кампания в архиве',
    needCharacter: 'Персонажа за этим местом ещё нет.',
    attach: 'Персонаж с другого устройства — по коду',
    attachHint:
      'Введите код персонажа, которого создали раньше на другом устройстве, — он пойдёт в заявку со всеми своими вещами, уровнями и историей.',
    code: 'Код персонажа',
    attachSubmit: 'Взять этого персонажа',
    newCharacter: 'Создать нового',
    deviceChange: 'Я на новом устройстве',
    deviceChangeHint:
      'Если вы уже за столом, а устройство новое, ссылка не поможет: по ней приходят с новым персонажем. Введите код своего — мастер увидит заявку и пустит вас обратно.',
    deviceChangeSubmit: 'Отправить заявку',
    failed: 'Не вышло. Попробуйте ещё раз',
  },

  auth: {
    signIn: 'Вход',
    signUp: 'Регистрация мастера',
    email: 'Почта',
    password: 'Пароль',
    passwordHint: 'Не короче {{count}} знаков. Требований к составу нет — важна длина.',
    submitSignIn: 'Войти',
    submitSignUp: 'Зарегистрироваться',
    toSignUp: 'Ещё нет аккаунта',
    toSignIn: 'Уже есть аккаунт',
    forgot: 'Забыли пароль',
    forgotTitle: 'Восстановление пароля',
    forgotText: 'Пришлём ссылку на смену пароля. Она действует час.',
    forgotSubmit: 'Прислать ссылку',
    // Один и тот же ответ, зарегистрирован адрес или нет: разные
    // ответы позволяют перебором собрать список зарегистрированных.
    sent: 'Если такой адрес у нас есть, письмо уже ушло. Проверьте почту.',
    registered:
      'Письмо отправлено. Откройте ссылку из него — до этого вход не работает.',
    confirmTitle: 'Подтверждение почты',
    confirmWorking: 'Подтверждаем…',
    confirmDone: 'Почта подтверждена. Теперь можно войти.',
    resetTitle: 'Новый пароль',
    resetSubmit: 'Сменить пароль',
    resetDone: 'Пароль сменён, и все сессии закрыты. Войдите заново.',
    signOut: 'Выйти',
    signOutAll: 'Выйти со всех устройств',
    signedInAs: 'Вы вошли как {{email}}',
    working: 'Отправляем…',
    // Письмо ставится в очередь и уходит отдельно (D41): между
    // «аккаунт создан» и «письмо в ящике» есть зазор, и попросить
    // ещё раз — единственное, что человек может сделать сам.
    resend: 'Отправить письмо ещё раз',
    resentDone:
      'Отправили ещё раз. Письмо идёт до минуты; проверьте и папку со спамом.',
    problem: {
      invalid_credentials: 'Адрес или пароль не подошли',
      email_not_confirmed: 'Почта не подтверждена: откройте ссылку из письма',
      invalid_token: 'Ссылка не действует: она устарела или уже использована',
      rate_limited: 'Слишком часто. Попробуйте через {{count}} с',
      session_expired: 'Сессия закончилась, войдите заново',
    },
    // Замечания к полям: сервер знает, что именно не так, и говорит
    // это словами, а не общим отказом.
    field: {
      email: 'Почта',
      password: 'Пароль',
    },
    invalid: {
      invalid_email: 'Почта: похоже, это не адрес',
      password_too_short: 'Пароль: не короче {{count}} знаков',
      password_too_long: 'Пароль: слишком длинный',
      required: '{{field}}: заполните поле',
      invalid: '{{field}}: значение не подошло',
    },
    unreachable: 'Сервер не отвечает. Проверьте связь и попробуйте снова',
    serverFailed: 'На сервере ошибка. Мы о ней уже знаем — попробуйте позже',
    failed: 'Не вышло. Попробуйте ещё раз',
    adopt: {
      title: 'Перенести персонажей на аккаунт',
      // Молча не переносим: за столом один ноутбук на партию, и драфты
      // на нём могут быть чужими (D33).
      text: 'На этом устройстве есть персонажи без кампании. Перенести их на аккаунт? Перенос односторонний: вернуть персонажа устройству нельзя.',
      submit: 'Перенести отмеченных',
      skip: 'Не переносить',
      done: 'Перенесено: {{count}}',
    },
  },

  start: {
    title: 'С чего начнём',
    createTitle: 'Создать персонажа',
    createText:
      'Персонаж без кампании: характеристики, расчёт и лист работают. Позже мастер подберёт его по коду.',
    create: 'Создать персонажа',
    joinTitle: 'Присоединиться к кампании',
    joinText: 'Вставьте ссылку, которую прислал мастер.',
    link: 'Ссылка от мастера',
    join: 'Войти',
    mine: 'Мои персонажи: {{count}}',
    refusal: {
      empty: 'Вставьте ссылку от мастера',
      'not-a-link': 'Это не похоже на ссылку от мастера',
      'no-token': 'В ссылке нет приглашения — похоже, она обрезана',
    },
  },

  edit: {
    title: 'Правка выборов',
    draftHint:
      'Персонаж без кампании — правка применяется сразу. Слоты, которых при новом выборе нет, исчезнут; новые придут пустыми, и без них не сохранить.',
    seatedHint:
      'Персонаж за столом — правка уйдёт мастеру и заработает после его решения. До тех пор лист не меняется.',
    classLocked:
      'Класс на {{level}}-м уровне не меняется: от него зависят повышения. Чтобы сменить класс, откатите повышения.',
    save: 'Сохранить правку',
    send: 'Отправить мастеру',
    open: 'Изменить выборы',
    openSeated: 'Попросить правку',
    ownContent: 'Нужной записи нет? Завести свою',
    pending: 'Правка ждёт решения мастера. Лист пока прежний.',
    rejected: 'Мастер отклонил правку.',
    rejectedWhy: 'Мастер отклонил правку: {{note}}',
    changesTitle: 'Что меняется',
    noChanges: 'Правка ничего не меняет.',
    was: 'Было: {{value}}',
    will: 'Станет: {{value}}',
    empty: 'пусто',
    line: {
      name: 'Имя',
      class: 'Класс',
      species: 'Вид',
      background: 'Предыстория',
      ability_method: 'Способ характеристик',
      abilities: 'Характеристики',
      ability_increase: 'Прибавки предыстории',
    },
  },
  whatsNew: {
    title: 'Что нового',
    hint: 'Последние перемены — коротко и без технических слов. Подробный список изменений ведут разработчики.',
    version: 'Версия {{version}} · {{date}}',
    back: 'Назад',
    items: {
      v0_4_12_1: 'Лист можно распечатать: меню «Экспортировать» → «Распечатать». Одна страница A4, как привычный бумажный бланк, чёрно-белая.',
      v0_4_12_2: 'На бумаге те же числа, что на экране, плюс подкласс, черта происхождения и счётчики с клетками под карандаш. Чего приложение не знает — мировоззрение, спасброски от смерти, — оставлено пустым, чтобы вписать от руки.',
      v0_4_12_3: '«Пассивная Внимательность» теперь «Пассивное Восприятие» — как и сам навык. Атаки заговорами называют тип урона по-русски.',
      v0_4_11_1: 'Счётчики ресурсов называются по-русски: «Божественный канал», «Второе дыхание», «Ярость» — и на листе, и у мастера.',
      v0_4_11_2: 'Навык, который уже дала предыстория, больше не тратит выбор: в списке он погашен и подписан, откуда он у вас есть.',
      v0_4_11_3: 'Все предметы названы по-русски — и в списке, из которого мастер выдаёт вещи, и в образцах редактора записей.',
      v0_4_11_4: 'Три оружия у монстров названы так же, как в снаряжении: Дубинка, Одноручный арбалет, Пистоль.',
      v0_4_11_5: 'Появился этот экран.',
      v0_4_10_1: 'У кампании есть стартовый уровень. Персонаж всё равно собирается по шагам: создание, потом повышения подряд — прямо в заявке по ссылке.',
      v0_4_10_2: 'Мастер видит у заявки один список того, что не сходится с кампанией, и по каждому пункту — чем грозит и что можно сделать. Отказ приходит игроку с причиной.',
      v0_4_10_3: 'Сделанные выборы можно поправить. Персонаж без кампании правится сразу, персонаж за столом — через мастера.',
      v0_4_10_4: 'Игрок без аккаунта может завести свою запись — например, заклинание, которого нет в книге. За столом она заработает, когда мастер её примет.',
      v0_4_10_5: 'Названия поменялись: «Драконьи предки» теперь «Драконье наследие», «Универсальность» — «Разносторонность». Одна и та же черта называлась двумя словами на двух экранах.',
      v0_4_9_1: 'Названия поменялись: «умения» теперь «навыки», «происхождение» — «предыстория». Так их называет справочник dnd.su, и так их проще найти.',
      v0_4_9_2: 'Черта, выбранная при создании, наконец даёт то, что обещает: «Одарённый» и «Умелый» спрашивают свои выборы.',
      v0_4_9_3: 'Ссылки на правила ведут в раздел dnd.su для редакции 2024 года.',
      v0_4_8_1: 'Рядом с непонятными словами появился знак вопроса: нажмите — откроется, что это значит, и ссылка на dnd.su.',
    },
  },
  wizard: {
    title: 'Создание персонажа',
    name: 'Имя персонажа',
    targetLevel: 'Стартовый уровень',
    targetOne: 'Персонаж начнёт с первого уровня.',
    targetHint_one:
      'После создания откроется повышение: ещё {{count}} уровень. Прервать можно в любой момент — персонаж останется на достигнутом.',
    targetHint_few:
      'После создания откроются повышения: ещё {{count}} уровня. Прервать можно в любой момент — персонаж останется на достигнутом.',
    targetHint_many:
      'После создания откроются повышения: ещё {{count}} уровней. Прервать можно в любой момент — персонаж останется на достигнутом.',
    choose: 'Выбрать: {{count}}',
    search: 'Поиск по списку',
    moreOptions: 'Ещё {{count}} в списке — найдите поиском',
    chosen: 'Выбрано: {{count}}',
    taken: {
      granted: 'уже есть: {{source}}',
      picked: 'уже выбрано: {{slot}}',
    },
    submit: 'Создать',
    cancel: 'Отмена',
    loading: 'План создания загружается…',
    draftNotice: 'Персонаж создаётся без кампании. Позже мастер подберёт его по коду.',
    abilities: {
      title: 'Характеристики',
      method: 'Способ',
      standard_array: 'Стандартный набор',
      point_buy: 'Покупка очков',
      rolls: 'Броски',
      budget: 'Осталось очков: {{remaining}} из {{budget}}',
      overBudget: 'Потрачено больше бюджета на {{over}}',
      poolTitle: 'Выпавшие значения',
      poolRollsHint:
        'Шесть чисел: {{dice}}d{{die}}, берутся лучшие {{keep}} — и так {{count}} раз',
      poolFixedHint: 'Стандартный набор задан правилами и не меняется',
      rollPool: 'Бросить кости',
      poolOrderHint: 'Порядок ввода ни на что не влияет: раздаёте вы сами',
      poolItem: 'Бросок {{index}}',
      poolLeft: 'Осталось раздать: {{values}}',
      poolDone: 'Все значения раздал',
      assignEmpty: 'не выбрано',
    },
    increase: {
      title: 'Прибавки предыстории',
      hint: 'Раскладки: {{patterns}}',
      none: 'Без прибавки',
      left: 'Осталось раздать: {{values}}',
      done: 'Все прибавки розданы',
    },
    equipment: {
      times: '{{count}} × {{name}}',
      gold: '{{count}} зм',
      last: '{{head}} и {{tail}}',
    },
    invalid: {
      title: 'Пока не хватает вот чего',
      name: 'Впишите имя персонажа',
      slot: 'Выберите: {{count}}',
      abilities: 'Раздайте все шесть значений',
    },
    problems: {
      title: 'Сервер не принял выборы',
      'unknown-slot': 'Этого выбора в плане создания нет',
      'unknown-option': 'Такого варианта здесь нет',
      'wrong-count': 'Нужно выбрать {{count}}, а выбрано {{chosen}}',
      'duplicate-pick': 'Один и тот же вариант выбран дважды',
      'unknown-content': 'Выбранной записи нет в справочнике',
      'not-standard-array': 'Значения не совпадают со стандартным набором',
      'point-buy-over-budget': 'Потрачено больше очков, чем есть',
      'score-not-purchasable': 'Такое значение купить нельзя',
      'roll-out-of-range': 'Такое число этим броском не выпадает',
      'wrong-roll-count': 'Чисел брошено не столько, сколько нужно',
      'assignment-mismatch': 'Розданы не те числа, что выпали',
      'increase-pattern': 'Такой раскладки прибавок правила не дают',
      'increase-not-offered': 'Предыстория не даёт прибавку в эту характеристику',
      'increase-over-max': 'Прибавка поднимает характеристику выше предела',
      'class-locked':
        'Класс меняется только на первом уровне: от него зависят повышения. Откатите их и пройдите заново',
      'levels-broken':
        'После правки не собираются повышения уровня: откатите их и пройдите заново',
      other: 'Выбор не подошёл',
    },
  },
  update: {
    available: 'Доступно обновление',
    reload: 'Обновить',
  },
  abilities: {
    strength: 'Сила',
    dexterity: 'Ловкость',
    constitution: 'Телосложение',
    intelligence: 'Интеллект',
    wisdom: 'Мудрость',
    charisma: 'Харизма',
  },
  abilitiesShort: {
    strength: 'СИЛ',
    dexterity: 'ЛОВ',
    constitution: 'ТЕЛ',
    intelligence: 'ИНТ',
    wisdom: 'МДР',
    charisma: 'ХАР',
  },
  trait: {
    // Ключи движка: слева вид владения, справа сам ключ.
    save: 'Спасбросок: {{name}}',
    skill: 'Навык: {{name}}',
    check: 'Проверки {{ability}}: {{skill}}',
    condition: {
      charmed: 'Спасброски от очарования',
      frightened: 'Спасброски от испуга',
      poisoned: 'Спасброски от отравления',
    },
    armor: {
      light: 'Лёгкие доспехи',
      medium: 'Средние доспехи',
      heavy: 'Тяжёлые доспехи',
      shields: 'Щиты',
    },
    weapon: {
      simple: 'Простое оружие',
      martial: 'Воинское оружие',
    },
    tool: {
      calligraphers_supplies: 'Письменные принадлежности',
      herbalism_kit: 'Набор травника',
      thieves_tools: 'Воровские инструменты',
    },
    damage: {
      bludgeoning: 'Дробящий урон',
      piercing: 'Колющий урон',
      slashing: 'Рубящий урон',
      cold: 'Холод',
      fire: 'Огонь',
      force: 'Силовое поле',
      poison: 'Яд',
      // Типы урона заговоров. Без них атака «Брызгами кислоты»
      // показывала тип урона ключом — `acid`, — и на экране, и на бумаге.
      acid: 'Кислота',
      lightning: 'Электричество',
      necrotic: 'Некротическая энергия',
      psychic: 'Психическая энергия',
      radiant: 'Излучение',
      thunder: 'Звук',
    },
  },
  notFound: {
    title: 'Такого адреса нет',
    text: 'Мы не знаем, что показать по адресу {{path}}.',
    home: 'К списку персонажей',
  },
  errors: {
    network: 'Сервер недоступен. Изменения требуют сети (D9).',
    unknown: 'Что-то пошло не так.',
    // Сервер ответил и объяснил — это не сеть. Раньше отказ по полю
    // показывался как «сервер недоступен», и мастер шёл чинить wi-fi,
    // пока сервер называл незаполненный ключ.
    rejected: 'Сервер не принял запись. Проверьте поля.',
    badFields: 'Не приняты поля: {{fields}}.',
    serverFailed: 'Сервер ответил ошибкой. Это на нашей стороне.',
    // Лист не посчитался — это не отказ сети: сеть работает, ссылка
    // верная, а не сходится расчёт. Причина называется всегда:
    // «ошибка расчёта» без причины чинить нечем.
    calculation: {
      title: 'Лист не посчитался',
      'unknown-path': 'В расчёте нет величины «{{path}}».',
      'cyclic-ref': 'Величины ссылаются по кругу: {{chain}}.',
      'conflicting-set': 'Величину «{{target}}» задают сразу несколько источников.',
      'missing-base': 'У величины «{{target}}» есть прибавки, но нет основы.',
      'duplicate-step': 'В журнале два шага с номером {{stepIndex}}.',
      'unknown-content':
        'В журнале есть ссылка на запись «{{id}}», которой больше нет.',
      'unexpected-target-kind':
        'Над величиной «{{target}}» смешаны операции разного вида.',
      'unexpected-context-value': 'По пути «{{path}}» лежит значение не того вида.',
      unknown: 'Причину назвать не удалось.',
    },
  },
} as const

const en = {
  transfer: {
    exportHint:
      'The file holds the journal of decisions, not computed numbers: the sheet is rebuilt on import. The text sheet does not load back.',
    import: 'Load a character from a file',
    importHint: 'A file exported by this app.',
    tooLarge: 'The file is too large to be a character file.',
    refused: 'File {{file}} was not accepted:',
    adopt: 'Add these records to my own and import ({{count}})',
    imported: 'Character loaded.',
    menu: 'Export',
    toDevice: 'To another device',
    toDeviceHint: 'The character file: load it on another device.',
    toText: 'As text',
    toTextHint: 'The sheet in Markdown, for notes; it does not load back.',
    toPrint: 'Print',
    toPrintHint: 'A one-page A4 sheet.',
  },
  print: {
    title: 'Printable sheet',
    hint: 'One A4 page. The sheet sets its own margins and scale, so leave them as they are in the print dialog. For a file instead of paper, choose “Save as PDF” there.',
    print: 'Print',
    back: 'Back to the sheet',
    loading: 'Preparing the sheet…',
    classLevel: '{{name}} {{level}}',
    levelOnly: 'Level {{level}}',
    xpOf: '{{total}} / {{next}}',
    save: 'Saving throw',
    more: '{{count}} more — in the app',
    label: {
      name: 'Character name',
      classLevel: 'Class & level',
      subclass: 'Subclass',
      species: 'Species',
      background: 'Background',
      originFeat: 'Origin feat',
      player: 'Player name',
      alignment: 'Alignment',
      xp: 'XP / next level',
      inspiration: 'Heroic Inspiration',
      hpMax: 'Max',
      remaining: 'Current',
      total: 'Total',
      spent: 'Spent',
      deathSaves: 'Death saving throws',
      failures: 'Failures',
      successes: 'Successes',
      attackName: 'Name',
      damageType: 'Damage type',
      spellAbility: 'Spellcasting ability',
      saveDcHint: '8 + proficiency bonus + modifier',
      attackHint: 'proficiency bonus + modifier',
      resources: 'Counters',
      notes: 'Notes',
    },
    coin: {
      cp: 'CP',
      sp: 'SP',
      ep: 'EP',
      gp: 'GP',
      pp: 'PP',
    },
    icons: 'Icons: game-icons.net — Delapouite, Lorc, Sbed, Skoll; CC BY 3.0.',
  },
  markdown: {
    resources: 'Counters',
    summary: 'Summary',
    ability: 'Ability',
    score: 'Score',
    mod: 'Modifier',
    hit: 'To hit',
    xp: 'Experience',
    equipped: 'worn',
    coins: 'Coins',
    weight: 'Weight',
    conditions: 'Conditions',
    spellLevel: 'level {{level}}',
    attribution:
      'Rules text from SRD 5.2 (Wizards of the Coast), CC BY 4.0. Sheet produced by Rollcall.',
  },
  custom: {
    title: 'My records',
    hint: 'Records the SRD does not have: your own weapon, a subclass from another book, a class invented at the table. The format is the same as the SRD, so the sheet counts them the same way. They reach a table through the campaign setting «Custom content».',
    new: 'New record',
    record: 'the entry as a whole',
    back: 'Back',
    next: 'Next',
    save: 'Save',
    cancel: 'Cancel',
    empty: 'Nothing here yet.',
    edit: 'Edit',
    remove: 'Delete',
    removeUsed: 'Delete ({{count}} on sheets)',
    unused: 'not used on any sheet',
    usedBy: 'characters using it: {{count}}',
    usedWarning:
      'This record is on {{count}} sheet(s). Changing its mechanics recalculates them.',
    editorTitle: 'Content record',
    editorHint:
      'The steps run in order; going back keeps what you typed. The record reaches the campaign only after you save from the last step.',
    step: 'Step {{step}} of {{total}}',
    steps: {
      type: 'what to create',
      base: 'from scratch or a copy',
      fields: 'record fields',
      effects: 'effects',
      preview: 'how it looks',
    },
    type: {
      item: 'Item',
      spell: 'Spell',
      subclass: 'Subclass',
      species: 'Species',
      background: 'Background',
      feat: 'Feat',
      class: 'Class',
    },
    fromScratch: 'From scratch',
    copyHint: 'Or start from an existing record — the form opens filled in.',
    searchLabel: 'Search by name',
    own: 'mine',
    copy: 'Use as a base',
    key: 'Record key',
    keyHint:
      'Lowercase latin, digits and underscore. The key never changes after creation: sheets refer to it.',
    note: 'Where it came from (a note)',
    noEffects: 'No effects: this record computes nothing.',
    effectsPreview: 'What it does to a sheet',
    noName: 'Unnamed',
    problemAt: '{{path}}: {{message}}',
    form: {
      unsupported:
        'The editor cannot fill in «{{field}}». Such a field comes from copying an existing record.',
      variant: 'Kind',
      addItem: 'Add',
      removeItem: 'Remove',
      present: 'fill in',
      refHint: 'srd:key or u:owner:key',
    },
    choice: {
      name: 'What the choice is called',
      count: 'How many to take',
      from: 'Where the options come from',
      list: 'List by reference',
      options: 'References, comma separated',
      add: 'Add a choice',
      remove: 'Remove the choice',
      type: {
        skill: 'Any skill',
        language: 'Any language',
        feat: 'Any feat',
        spell: 'Any spell',
        item: 'Any item',
      },
    },
    effects: {
      target: 'What changes',
      opLabel: 'Operation',
      amount: 'By how much',
      min: 'At least',
      max: 'At most',
      cap: 'Contribution cap',
      kind: 'Number or value',
      kindNumber: 'number',
      kindRef: 'sheet value',
      ref: 'Value',
      trait: 'Trait',
      add: 'Add an effect',
      addTrait: 'Add a trait',
      remove: 'Remove',
      summary: 'What comes out',
      conflict:
        '«{{target}}» already has a «set». Two «set» on one value cannot both apply: mutual exclusion is written with conditions (D18.3).',
      limitsTitle: 'What cannot be expressed here',
      limits:
        'Recharge, legendary actions, reactions and damage types with resistances are not expressible in this format — neither for the SRD nor for your own records. Write them as text in the description and play them by hand.',
      and: ' and ',
      opText: '{{target}}: {{op}} {{value}}',
      grantText: '{{target}}: {{value}}',
      cappedText: '{{amount}}, but no more than {{max}}',
      clampText: 'from {{min}} to {{max}}',
      condAdd: 'Add a condition',
      condRemove: 'Remove the condition',
      condPath: 'Condition: what to look at',
      condOp: 'Condition: how to compare',
      condValue: 'Condition: value',
      self: 'this record (for example, while it is worn)',
      op: {
        set: 'set',
        add: 'add',
        add_capped: 'add, capped',
        mul: 'multiply by',
        clamp: 'clamp',
      },
      condOpName: {
        eq: 'equals',
        neq: 'differs from',
        gte: 'at least',
        lte: 'at most',
        has: 'has',
        has_not: 'has not',
      },
      cond: {
        eq: '{{path}} = {{value}}',
        neq: '{{path}} ≠ {{value}}',
        gte: '{{path}} ≥ {{value}}',
        lte: '{{path}} ≤ {{value}}',
        in: '{{path}} among: {{value}}',
        has: 'has {{value}}',
        has_not: 'has no {{value}}',
      },
    },
    field: {
      name: 'Name',
      text: 'Description',
      effects: 'Effects',
      mechanics: 'Mechanics',
      ability: 'Ability',
      abilityScores: 'Ability scores',
      acBonus: 'AC bonus',
      amount: 'Amount',
      armorTraining: 'Armor training',
      attack: 'Attack',
      attackDamage: 'Attack damage',
      attunement: 'Attunement',
      baseAc: 'Base AC',
      benefits: 'Benefits',
      cantrips: 'Cantrips',
      carryingCapacityLb: 'Carrying capacity, lb',
      castingTime: 'Casting time',
      category: 'Category',
      changePrepared: 'Changing prepared spells',
      choices: 'Choices',
      choose: 'How many to choose',
      classes: 'Classes',
      components: 'Components',
      concentration: 'Concentration',
      cost: 'Cost',
      creatureType: 'Creature type',
      currency: 'Currency',
      damage: 'Damage',
      damageType: 'Damage type',
      dexterityBonus: 'Dexterity bonus',
      duration: 'Duration',
      equipment: 'Equipment',
      features: 'Features',
      from: 'From',
      goldGp: 'Gold',
      hitDie: 'Hit die',
      id: 'Key',
      item: 'Entry',
      items: 'Entries',
      key: 'Key',
      kind: 'Kind',
      label: 'Label',
      level: 'Level',
      levels: 'Levels',
      limit: 'Limit',
      mastery: 'Weapon mastery',
      notation: 'Dice',
      onSuccess: 'On success',
      option: 'Option',
      options: 'Options',
      originFeat: 'Origin feat',
      pact: 'Pact',
      parentClass: 'Class',
      prepared: 'Preparation',
      prerequisite: 'Prerequisite',
      primaryAbility: 'Primary ability',
      proficiencyBonus: 'Proficiency bonus',
      prompt: 'Prompt',
      properties: 'Properties',
      quantity: 'Quantity',
      range: 'Range',
      rarity: 'Rarity',
      reach: 'Reach',
      ritual: 'Ritual',
      save: 'Saving throw',
      savingThrowProficiencies: 'Saving throw proficiencies',
      school: 'School',
      sizes: 'Sizes',
      skillProficiencies: 'Skill proficiencies',
      slots: 'Slots',
      speedFt: 'Speed, ft',
      spellcasting: 'Spellcasting',
      startingEquipment: 'Starting equipment',
      stealthDisadvantage: 'Stealth disadvantage',
      strengthRequirement: 'Strength requirement',
      toolProficiencies: 'Tool proficiencies',
      toolProficiency: 'Tool proficiency',
      traits: 'Traits',
      type: 'Type',
      upgradeAt: 'Upgrades at level',
      value: 'Value',
      weaponProficiencies: 'Weapon proficiencies',
      weightLb: 'Weight, lb',
      weight_class: 'Weight class',
      when: 'When',
    },
    option: {
      armor: 'armor',
      shield: 'shield',
      shields: 'shields',
      weapon: 'weapon',
      gear: 'gear',
      tool: 'tool',
      magic: 'magic item',
      light: 'light',
      medium: 'medium',
      heavy: 'heavy',
      melee: 'melee',
      ranged: 'ranged',
      simple: 'simple',
      martial: 'martial',
      bludgeoning: 'bludgeoning',
      piercing: 'piercing',
      slashing: 'slashing',
      common: 'common',
      uncommon: 'uncommon',
      rare: 'rare',
      very_rare: 'very rare',
      legendary: 'legendary',
      varies: 'varies',
      none: 'none',
      cp: 'copper',
      sp: 'silver',
      gp: 'gold',
      abjuration: 'abjuration',
      conjuration: 'conjuration',
      divination: 'divination',
      enchantment: 'enchantment',
      evocation: 'evocation',
      illusion: 'illusion',
      necromancy: 'necromancy',
      transmutation: 'transmutation',
      strength: 'Strength',
      dexterity: 'Dexterity',
      constitution: 'Constitution',
      intelligence: 'Intelligence',
      wisdom: 'Wisdom',
      charisma: 'Charisma',
      small: 'small',
      large: 'large',
      origin: 'origin',
      general: 'general',
      fighting_style: 'fighting style',
      epic_boon: 'epic boon',
      full: 'full',
      catalogue: 'from the catalogue',
      feat: 'feat',
      item: 'item',
      language: 'language',
      level: 'level',
      list: 'list',
      long_rest: 'long rest',
      max_2: 'level 2 or lower',
      skill: 'skill',
      spell: 'spell',
    },
    path: {
      ac: 'AC',
      initiative: 'Initiative',
      level: 'Level',
      languages: 'Languages',
      proficiencies: 'Proficiencies',
      proficiency_bonus: 'Proficiency bonus',
      resistances: 'Resistances',
      advantages: 'Advantages',
      disadvantages: 'Disadvantages',
      worn: 'Worn',
      speed: 'Speed',
      'hp.max': 'Hit point maximum',
      'senses.darkvision': 'Darkvision',
      'speeds.swim': 'Swim speed',
      'speeds.fly': 'Fly speed',
      'speeds.climb': 'Climb speed',
      'resources.uses': 'Uses',
      'passive.perception': 'Passive Perception',
      'spell.attack': 'Spell attack',
      'spell.save_dc': 'Spell save DC',
      'saves.str': 'Strength save',
      'saves.dex': 'Dexterity save',
      'saves.con': 'Constitution save',
      'saves.int': 'Intelligence save',
      'saves.wis': 'Wisdom save',
      'saves.cha': 'Charisma save',
      'check.str': 'Strength check',
      'check.dex': 'Dexterity check',
      'check.con': 'Constitution check',
      'check.int': 'Intelligence check',
      'check.wis': 'Wisdom check',
      'check.cha': 'Charisma check',
      'abilities.str.score': 'Strength',
      'abilities.dex.score': 'Dexterity',
      'abilities.con.score': 'Constitution',
      'abilities.int.score': 'Intelligence',
      'abilities.wis.score': 'Wisdom',
      'abilities.cha.score': 'Charisma',
      'abilities.str.mod': 'Strength modifier',
      'abilities.dex.mod': 'Dexterity modifier',
      'abilities.con.mod': 'Constitution modifier',
      'abilities.int.mod': 'Intelligence modifier',
      'abilities.wis.mod': 'Wisdom modifier',
      'abilities.cha.mod': 'Charisma modifier',
      'carry.weightLb': 'Weight carried',
      'carry.capacityLb': 'Carrying capacity',
      'carry.over': 'Overloaded',
      'state.equipped': 'Worn',
      'state.concentration': 'Concentration',
      'state.activeEffects': 'Active effects',
    },
  },
  about: {
    version: 'Version {{version}} · build {{commit}}',
    srd: 'Includes material from SRD 5.2 by Wizards of the Coast:',
    title: 'About',
    srdTitle: 'Source material',
    back: 'Back',
  },
  app: {
    title: 'Character sheet',
    name: 'Character sheet and campaign',
    hint: 'How the “{{section}}” section works',
  },
  theme: {
    title: 'Theme',
    toggle: 'Switch theme',
    light: 'Light theme',
    dark: 'Dark theme',
  },
  home: {
    title: 'My games',
  },
  menu: {
    title: 'Menu',
    close: 'Close the menu',
    home: 'Home',
  },
  language: {
    title: 'Language',
    toggle: 'Switch language',
    ru: 'Русский',
    en: 'English',
    codeRu: 'РУ',
    codeEn: 'EN',
  },
  tabs: {
    combat: 'Combat',
    spells: 'Spells',
    inventory: 'Inventory',
    character: 'Character',
    empty: 'Coming in a later pass.',
  },
  resources: {
    count: '{{current}} of {{max}}',
    spend: 'Spend',
    restore: 'Restore',
    own: 'Your own counters',
    addOpen: 'Add a counter',
    add: 'Add',
    remove: 'Remove',
    name: 'Name',
    max: 'Maximum',
    recoveryLabel: 'Recovered by',
    maxNumber: 'a plain number',
    maxValue: 'How many',
    min: 'At least',
    cancel: 'Cancel',
    recovery: {
      none: 'not recovered by resting',
      long: 'recovered on a Long Rest',
      short: 'recovered on a Short Rest',
      shortSome: 'a Short Rest returns {{uses}}, a Long Rest returns all',
    },
  },
  details: {
    title: 'About the character',
    empty: 'Nothing written about this character yet.',
    edit: 'Edit',
    save: 'Save',
    name: 'Name',
    appearance: 'Appearance',
    quirk: 'Quirk',
    motivation: 'Motivation',
    backstory: 'Backstory',
    weakness: 'Weakness',
    notes: 'Notes',
  },
  sheet: {
    level: 'Level',
    levelShort: 'level {{level}}',
    ac: 'Armor Class',
    hpMax: 'Hit Point maximum',
    initiative: 'Initiative',
    speed: 'Speed',
    proficiencyBonus: 'Proficiency Bonus',
    darkvision: 'Darkvision',
    passivePerception: 'Passive Perception',
    uses: 'Uses',
    climb: 'Climb Speed',
    fly: 'Fly Speed',
    swim: 'Swim Speed',
    abilities: 'Ability scores',
    modifier: 'modifier {{value}}',
    score: 'score {{value}}',
    saves: 'Saving throws',
    skills: 'Skills',
    proficient: 'proficient',
    proficientIn: '{{ability}} · proficient',
    traits: 'Proficiencies and traits',
    proficiencies: 'Proficiencies',
    languages: 'Languages',
    resistances: 'Resistances',
    advantages: 'Advantages',
    disadvantages: 'Disadvantages',
    features: 'Traits and features',
    rest: 'Other values',
    draft: 'Draft: this character is in no campaign',
    code: 'Character code',
    copyCode: 'Copy the code',
    codeCopied: 'Code copied',
    codeHint:
      'You need this code if you join a table from another device: type it in on the invitation screen.',
    abilityCheck: '{{name}} check',
    hpLevel: 'Hit points for level {{level}}',
    traitKind: {
      save: 'Saving throws',
      skill: 'Skills',
      armor: 'Armor',
      weapon: 'Weapons',
      tool: 'Tools',
      check: 'Checks',
      other: 'Other',
    },
  },
  inventory: {
    title: 'Inventory',
    empty: 'No items.',
    equipped: 'Worn and wielded',
    carried: 'Carried',
    coins: 'Coins',
    coinsValue: '{{count}} GP',
    quantity: '{{count}} pcs',
    weight: '{{count}} lb',
    weightTotal: 'Weight',
    weightOf: '{{total}} lb of {{carry}} lb',
    carryHint: 'Carrying more than {{carry}} lb drops Speed to five feet.',
    over: 'Over capacity: the load is above {{carry}} lb, Speed on the sheet is {{speed}} ft.',
    dragHint: 'Dragging, pushing and lifting go up to {{drag}} lb.',
    unknownWeight:
      'Weight not counted for: {{items}}. For these it depends on the variant.',
    noRecord: 'No content record: nothing can be done with this line.',
    equip: 'Equip',
    unequip: 'Take off',
    drop: 'Drop',
    count: 'Quantity',
    setCount: 'Change',
    busy: 'Sending…',
    refusal: {
      'unknown-item': 'There is no such item in the content.',
      'not-in-inventory': 'The character does not have this item.',
      'not-a-record': 'This is not a piece of equipment.',
      'already-equipped': 'Already worn.',
      'not-equipped': 'Not worn.',
      'not-equippable': 'This is not something you wear.',
      'armor-already-worn': 'Armor is already worn: {{blockedBy}}. Take it off first.',
      'shield-already-held':
        'A shield is already held: {{blockedBy}}. There is one hand.',
      'bad-count': 'That quantity does not work.',
    },
    less: '{{name}}: one less',
    more: '{{name}}: one more',
    details: 'Description',
    dropConfirm: 'Drop “{{name}}”? You have {{count}}.',
    dropOne: 'Drop one',
    dropAll: 'Drop all',
    cancel: 'Cancel',
  },
  combat: {
    title: 'Combat',
    hp: 'Hit Points',
    hpOf: '{{current}} of {{max}}',
    temp: 'Temporary Hit Points',
    tempValue: '+{{count}} temporary',
    hitDie: 'Hit Point Die',
    hitDiceValue: '{{count}} × {{die}}',
    attacks: 'Attacks',
    noAttacks: 'No weapons: pick one up on the Inventory tab.',
    inHands: 'In hand',
    carried: 'Carried',
    attack: 'Attack',
    damage: 'Damage',
    versatile: 'two-handed {{notation}}',
    proficient: 'proficient',
    noProficiency: 'not proficient',
    spellAttack: 'cantrip',
    ranged: 'ranged',
    melee: 'melee',
    rollAttack: 'Attack roll',
    rollDamage: 'Damage roll',
    change: 'Change Hit Points',
    damageAction: 'Damage',
    healAction: 'Healing',
    tempAction: 'Temporary',
    amount: 'How much',
    rest: 'Long Rest',
    shortRest: 'Short Rest',
    spendDie: 'Spend a Hit Point Die ({{left}} × {{die}})',
    hitDiceSpent: 'spent {{count}}',
    restHint: 'Restores Hit Points and spell slots, drops Concentration.',
    refusal: {
      'bad-amount': 'Hit Points must be a whole non-negative number.',
      'already-full': 'Hit Points are already at maximum.',
      'already-down': 'Hit Points are already at zero.',
      'no-hit-dice': 'No Hit Point Dice left: a Long Rest brings them back.',
      'no-hit-die': 'The character has no Hit Point Die: no class chosen.',
    },
    attackOf: 'Attack: {{name}}',
    damageOf: 'Damage: {{name}}',
    shortRestHint: 'Heals nothing by itself: hit points come from a spent Hit Die.',
    spendDieHint: 'Roll {{die}} plus Constitution, minimum 1 hit point.',
    spendDieFull: 'With full hit points there is nothing to spend a die on.',
    spendDieNone: 'No Hit Dice left — until a Long Rest.',
    restedLong: 'Long Rest: hit points, Hit Dice and spell slots restored.',
    restedShort: 'Short Rest. Hit Dice left: {{left}}. A spent die is what heals.',
  },
  spells: {
    title: 'Spells',
    none: 'The {{class}} class does not cast spells.',
    noneNoClass: 'No class chosen, so there are no spells.',
    numbers: 'Spellcasting numbers',
    abilityIs: 'Spellcasting ability: {{ability}}',
    saveDc: 'Spell save DC',
    attack: 'Spell attack bonus',
    slots: 'Spell slots',
    slotLevel: 'Level {{level}}',
    slotsOf: '{{left}} of {{total}}',
    spend: 'Spend',
    restore: 'Restore',
    cantrips: 'Cantrips',
    prepared: 'Prepared',
    counts:
      'Cantrips {{cantrips}} of {{cantripsMax}}, spells {{prepared}} of {{preparedMax}}',
    filter: 'Level',
    filterAll: 'All',
    cantripShort: 'cantrip',
    levelShort: 'level {{level}}',
    add: 'Prepare',
    remove: 'Remove',
    available: 'Class list',
    availableHint: 'Prepare the day’s spells from this list.',
    availableCount:
      'The class list has {{count}} spells. Pick a level above to see them.',
    loading: 'Loading the list…',
    details: 'Details',
    schoolOf: {
      abjuration: 'Abjuration',
      conjuration: 'Conjuration',
      divination: 'Divination',
      enchantment: 'Enchantment',
      evocation: 'Evocation',
      illusion: 'Illusion',
      necromancy: 'Necromancy',
      transmutation: 'Transmutation',
    },
    labelOf: {
      'Prepared Spells': 'Prepared Spells',
      'Spells Known': 'Spells Known',
    },
    fact: {
      action: 'Action',
      bonusAction: 'Bonus Action',
      reaction: 'Reaction',
      orRitual: '{{time}} or Ritual',
      withTail: '{{head}}, {{tail}}',
      self: 'Self',
      touch: 'Touch',
      sight: 'Sight',
      unlimited: 'Unlimited',
      special: 'Special',
      feet: '{{count}} feet',
      instantaneous: 'Instantaneous',
      dispelled: 'Until dispelled',
      dispelledOrTriggered: 'Until dispelled or triggered',
      concentration: 'Concentration, up to {{amount}}',
      upTo: 'Up to {{amount}}',
      component: { v: 'V', s: 'S', m: 'M' },
      material: '{{letters}} ({{text}})',
    },
    // Формы `_few` и `_many` английскому не нужны, но набор ключей
    // в языках один: разошедшийся словарь ловится тестом, а не глазами.
    unit: {
      round_one: '{{count}} round',
      round_few: '{{count}} rounds',
      round_many: '{{count}} rounds',
      round_other: '{{count}} rounds',
      minute_one: '{{count}} minute',
      minute_few: '{{count}} minutes',
      minute_many: '{{count}} minutes',
      minute_other: '{{count}} minutes',
      hour_one: '{{count}} hour',
      hour_few: '{{count}} hours',
      hour_many: '{{count}} hours',
      hour_other: '{{count}} hours',
      day_one: '{{count}} day',
      day_few: '{{count}} days',
      day_many: '{{count}} days',
      day_other: '{{count}} days',
      mile_one: '{{count}} mile',
      mile_few: '{{count}} miles',
      mile_many: '{{count}} miles',
      mile_other: '{{count}} miles',
    },
    unitOf: {
      round_one: '{{count}} round',
      round_few: '{{count}} rounds',
      round_many: '{{count}} rounds',
      round_other: '{{count}} rounds',
      minute_one: '{{count}} minute',
      minute_few: '{{count}} minutes',
      minute_many: '{{count}} minutes',
      minute_other: '{{count}} minutes',
      hour_one: '{{count}} hour',
      hour_few: '{{count}} hours',
      hour_many: '{{count}} hours',
      hour_other: '{{count}} hours',
      day_one: '{{count}} day',
      day_few: '{{count}} days',
      day_many: '{{count}} days',
      day_other: '{{count}} days',
      mile_one: '{{count}} mile',
      mile_few: '{{count}} miles',
      mile_many: '{{count}} miles',
      mile_other: '{{count}} miles',
    },
    school: 'School',
    castingTime: 'Casting Time',
    range: 'Range',
    components: 'Components',
    duration: 'Duration',
    concentration: 'Concentration',
    ritual: 'Ritual',
    concentrateOn: 'Concentrate',
    concentrationDrop: 'Drop Concentration',
    concentrationNow: 'Now: {{spell}}',
    concentrationNone: 'Not concentrating on anything',
    concentrationReplaces: 'Will drop: {{spell}}',
    refusal: {
      'unknown-spell': 'There is no such spell in the content.',
      'not-a-spell': 'This is not a spell.',
      'not-in-class-list': 'This spell is not on the class list.',
      'already-prepared': 'Already prepared.',
      'not-prepared': 'Not prepared.',
      'no-room': 'No room left: that is what the class table allows.',
      'no-such-slot': 'The character has no slots of this level.',
      'no-slots-left': 'Slots of this level are spent.',
      'nothing-to-restore': 'Slots of this level are already full.',
      'not-a-caster': 'The class does not cast spells.',
      'not-concentration': 'This spell does not require Concentration.',
      'not-now': 'Not now: the class changes its prepared list on a rest or a level.',
      'change-limit': 'One spell at a time, and it has already been changed.',
      'cantrip-fixed': 'Cantrips change when you gain a class level.',
    },
    changeHintRest: 'The prepared list changes when you finish a Long Rest.',
    changeHintLevel: 'The prepared list changes when you gain a class level.',
    circle: 'Level {{level}}',
  },
  srd: {
    original: 'Original',
    originalHint: 'Our translation; SRD 5.2 by Wizards of the Coast, CC BY 4.0.',
  },
  terms: {
    hint: 'What “{{name}}” means',
    dndSu: 'More on dnd.su (2014 edition, in Russian)',
  },
  roll: {
    title: 'Rolls',
    empty: 'No rolls yet.',
    roll: 'Roll',
    rollValue: 'Roll: {{label}}',
    because: 'from the trait: {{trait}}',
    manual: 'Enter your own',
    manualHint: 'Rolled real dice — type the result in.',
    manualMark: 'by hand',
    result: 'Total {{total}}',
    dice: 'Dice: {{values}}',
    dropped: '({{value}})',
    modifiers: 'Modifiers: {{parts}}',
    notation: 'Roll notation',
    notationHint: 'For example, 2d6+3.',
    mode: 'How to roll',
    normal: 'Normally',
    advantage: 'With Advantage',
    disadvantage: 'With Disadvantage',
    reason: {
      attack: 'Attack: {{subject}}',
      damage: 'Damage: {{subject}}',
      check: 'Check: {{subject}}',
      save: 'Saving throw: {{subject}}',
      initiative: 'Initiative',
      ability_pool: 'Ability scores',
      hit_die: 'Hit Die',
      level_hp: 'Hit Points per level',
      custom: 'Custom roll',
    },
    source: {
      notation: 'from the notation',
    },
    hidden: 'Hidden roll',
    hiddenLabel: 'Roll hidden',
    hiddenHint:
      'Only you will see the result. The shared log keeps a mark that a roll happened.',
    refusal: 'The roll did not work out: check the notation.',
    critical: 'Critical success',
    fumble: 'Critical failure',
    panel: 'Rolls',
    open: 'Roll',
    close: 'Close the panel',
    last: 'Last roll',
    by: 'What to roll',
    again: 'Roll again — {{subject}}',
    modeHint: 'Applies to every roll here and to attacks on the Combat tab.',
    manualAdd: 'Record my own result',
  },
  level: {
    title: 'Level up',
    upTo: 'Level up to {{level}}',
    buildLeft_one:
      'Building the character up to level {{target}}: {{count}} level to go. You can stop at any point — the character keeps the level it reached.',
    buildLeft_few:
      'Building the character up to level {{target}}: {{count}} levels to go. You can stop at any point — the character keeps the level it reached.',
    buildLeft_many:
      'Building the character up to level {{target}}: {{count}} levels to go. You can stop at any point — the character keeps the level it reached.',
    building_one: 'Level {{level}}. {{count}} more to go.',
    building_few: 'Level {{level}}. {{count}} more to go.',
    building_many: 'Level {{level}}. {{count}} more to go.',
    open: 'Level up',
    stopBuild: 'Stop building',
    features: 'What the level brings',
    noFeatures: 'No new features at this level.',
    hitPoints: 'Hit Points for the level',
    hitPointsHint:
      'Hit Point Die {{die}} plus Constitution, or the fixed value {{fixed}}.',
    hpFixed: 'Take {{value}}',
    hpRoll: 'Roll {{die}}',
    confirm: 'Level up to {{level}}',
    undo: 'Undo level {{level}}',
    back: 'Back to the sheet',
    noPlan: 'Nowhere to go: the class has no next level.',
    current: 'Level {{level}}',
    done: 'Level up: now {{level}}.',
    undone: 'Level rolled back: level {{level}} again.',
    hpManual: 'I rolled it myself',
    hpManualField: 'What came up',
    hpManualHint: 'The number on a real {{die}} — without the Constitution bonus.',
    incomplete: 'Make the level choices first.',
  },
  provenance: {
    title: 'How this number is made',
    close: 'Close',
    explain: '{{label}}: {{value}}. Show the breakdown',
    journal: 'Chosen at creation',
    rulesetOther: 'System rules',
    ruleset: {
      character_creation: 'Base rules',
      proficiency_bonus: 'Proficiency Bonus',
      spell_save_dc: 'Spell save DC base',
    },
    op: {
      set: 'base {{value}}',
      add: '{{value}}',
      mul: '× {{value}}',
      addCapped: '{{value}}, capped at {{max}}',
      clamp: 'no lower than {{min}}, no higher than {{max}}',
      grant: 'trait “{{value}}”',
    },
  },
  characters: {
    title: 'Characters',
    empty: 'No characters yet.',
    create: 'Create a character',
    open: 'Open',
    loading: 'Loading…',
    level: 'Level {{level}}',
    back: 'To the character list',
    draft: 'No campaign',
    draftHint:
      'A character without a campaign is counted and levels up like any other — all they lack is a table. They join one through a seat link, not by sitting in your account.',
    inCampaign: 'In a campaign',
    missing:
      'There is no such character. A character made without a campaign opens only in the browser that made it — and only until you move it into an account.',
  },
  master: {
    party: 'Party',
    empty:
      'Nobody at the table yet. Send the players the campaign link — each one shows up here as soon as you approve their request.',
    commands: 'Commands',
    hide: 'Collapse',
    failed: 'The command did not go through.',
    level: 'level {{level}}',
    ac: 'AC',
    passive: 'Passive Perception',
    hp: {
      title: 'Hit Points',
      amount: 'How much',
      damage: 'Take Hit Points',
      heal: 'Heal',
      temp: 'Temporary',
    },
    item: {
      title: 'Item',
      choose: 'Pick an item',
      quantity: 'Count',
      give: 'Give',
      take: 'Take away',
    },
    condition: {
      title: 'Condition',
      choose: 'Pick a condition',
      apply: 'Apply',
      raise: 'One more level',
      remove: 'Remove',
      none: 'No conditions',
    },
    xp: {
      award: 'Experience',
      give: 'Award',
      total: 'XP: {{total}}',
      next: '{{at}} to level {{level}}',
      ready: 'Threshold reached: level-up available',
      self: 'Award to myself',
      selfHint:
        'This character is not at a table, so there is nobody else to award the XP. Once they join a campaign, the GM takes over.',
    },
    rest: {
      short: 'Short Rest',
      long: 'Long Rest',
      hint: 'Everyone rests at once: Hit Points, Hit Dice and spell slots come back for every character at the table. A rest is not declared for one character alone.',
    },
    monsters: {
      search: 'Search',
      pick: 'Monster',
      none: 'not chosen',
      option_one: '{{name}} · CR {{challenge}} · {{count}} HP · AC {{ac}}',
      option_few: '{{name}} · CR {{challenge}} · {{count}} HP · AC {{ac}}',
      option_many: '{{name}} · CR {{challenge}} · {{count}} HP · AC {{ac}}',
      option_other: '{{name}} · CR {{challenge}} · {{count}} HP · AC {{ac}}',
      count: 'How many',
      add: 'Add to the order',
      hp: '{{current}} of {{max}} HP',
      ac: 'AC {{ac, number}}',
      down: 'down',
      amount: 'Amount',
      damage: 'Damage',
      heal: 'Heal',
      hidden: 'hidden roll',
      attack: '{{name}} {{bonus}} · {{damage}}',
    },
    combat: {
      title: 'Combat order',
      idle: 'No combat',
      round: 'Round {{round}}',
      empty: 'The order is empty.',
      startRolled: 'Start combat, roll for everyone',
      startEmpty: 'Start combat, everyone rolls',
      next: 'Next turn',
      end: 'End combat',
      now: 'acting',
      roll: 'Roll',
      remove: 'Remove',
      creature: 'Creature',
      add: 'Add a row',
    },
    rolls: {
      title: 'Rolls at the table',
      empty: 'No rolls yet.',
      hidden: 'roll hidden',
      secret: 'hidden',
    },
  },

  campaigns: {
    review: {
      title: 'What does not match the campaign',
      level: {
        what: 'The character is level {{character}}, the campaign starts at {{campaign}}.',
        riskAbove:
          'At the table they will be stronger than the rest: more hit points, more slots, extra features.',
        riskBelow:
          'At the table they will be weaker than the rest and will go down first in any fight.',
        waysAbove:
          'Accept as is, send back, or raise the campaign level. Lowering the character is not possible: that would undo the player’s choices.',
        waysBelow:
          'Accept as is — the player will level up the usual way — or send back.',
      },
      missing: {
        what: 'The character references records that exist nowhere: {{names}}.',
        risk: 'Such a character’s sheet will not build: the calculation hits a reference with nothing behind it.',
        ways: 'Send back. There is nothing to accept — there would be nothing to show.',
      },
      unapproved: {
        what: 'The player’s own records the campaign has not approved: {{names}}.',
        risk: 'Until you decide they do not work at the table: the campaign cannot see them, and the player’s numbers would differ from yours.',
        ways: 'Accept — the records move to the table and start working for this character — or send back with an explanation.',
      },
      disabled: {
        what: 'The character relies on custom content, and the campaign has it switched off: {{names}}.',
        risk: 'The flag keeps custom content out of character creation, but the decision here is yours: accept, and the records move to the table and work.',
        ways: 'Accept if the records suit you, or send back with an explanation.',
      },
      raise: 'Raise the campaign level to {{level}}',
    },
    title: 'My campaigns',
    empty: 'No campaigns yet. Start one — seats and links live inside it.',
    create: 'Start a campaign',
    name: 'Title',
    customContent: 'Use custom content',
    customContentHint:
      'Your own entries will be offered during character creation alongside the book ones. Add them in “Your own entries” — they belong to you, not to the campaign, and this checkbox connects them to the table.',
    customContentOpen: 'Your own entries',
    ruleset: 'Ruleset',
    'ruleset.dnd5e-2024': 'D&D 5e, 2024 revision',
    submit: 'Create',
    cancel: 'Cancel',
    open: 'Open',
    archive: 'Archive',
    unarchive: 'Bring back',
    archived: 'Archived',
    showArchived: 'Show archived',
    hideArchived: 'Hide archived',
    noDelete:
      'A campaign can be archived: it leaves the list, and everything that happened at the table stays. There is no delete — it would take the players\u2019 characters with it.',
    summary: '{{used}} of {{limit}} at the table',
    pending: 'Waiting for a decision: {{count}}',
    rename: 'Rename',
    save: 'Save',
    stream: {
      live: 'Connected: new things appear on their own',
      connecting: 'No connection, reconnecting…',
      offline: 'No connection. Reload the page',
    },
    invite: {
      title: 'Link and settings',
      show: 'Show',
      hide: 'Hide',
      about: 'How this works',
      hint: 'One link for the whole campaign, and it works until you revoke it. Drop it in the group chat: anyone can follow it, but you decide who sits down — every request waits for your approval.',
      create: 'Make a link',
      copy: 'Copy',
      copied: 'Copied',
      revoke: 'Revoke the link',
      limit: 'How many people you expect',
      limitHint:
        'This is a limit, not a list: there are no seats in advance, one appears when you approve a request. Once the limit runs out, new requests are declined on their own — you will see it and can raise the number.',
      startLevel: 'Starting level',
      startLevelHint:
        'The level new players arrive at. The character is still built step by step: creation, then level-ups. Players already at the table are not affected.',
      startLevelSave: 'Save the level',
      limitSave: 'Save',
      used: '{{used}} of {{limit}} taken',
    },
    seats: {
      title: 'At the table',
      empty: 'No seats yet. Add as many as you expect players.',
      emptyNow:
        'Nobody at the table yet. Send the link — and approve the first request.',
      add: 'Add a seat',
      label: 'Seat label',
      free: 'Free',
      pending: 'Waiting for approval',
      taken: 'Taken',
      invite: 'Issue a link',
      reissue: 'Issue a new one',
      revoke: 'Revoke the link',
      inviteReady: 'The link is issued. Copy it now — it will not be shown again.',
      copy: 'Copy',
      copied: 'Copied',
      expires: 'Works until {{at}}',
      release: 'Free the seat',
      releaseHint:
        'The player leaves the party, and their character stays with them — history, items and all. Nothing is lost: the app cannot delete other people\u2019s characters.',
      released: 'The seat is free, the character went back to the player',
      noCharacter: 'No character yet',
      noCharacterHint:
        'The player brings the character: they open your link and either create a new one or take one they already have. Characters kept in your account do not join a table on their own — they have to come through the link too.',
    },
    requests: {
      title: 'Requests',
      empty: 'No requests.',
      hint: 'The link may have been forwarded on, so look at who you are letting in: name, species, class and level are what tells your player apart from a stranger.',
      first_login: 'First login',
      device_change: 'Device change',
      edit: 'Choice edit',
      approveEdit: 'Accept the edit',
      note: 'What to tell the player if you decline',
      approve: 'Let in',
      reject: 'Decline',
      unknown: 'No name',
      level: 'Level {{level}}',
      origin: {
        new: 'New character',
        draft: 'Existing character',
      },
      autoRejected: 'Declined: no seats left',
      autoRejectedHint:
        'Raise the number of seats — and they can send the request again through the same link.',
    },
  },

  join: {
    title: 'Invitation',
    working: 'Checking the link…',
    pending:
      'The request is in. The GM will see it; until they approve, nothing about the party is known.',
    rejected: {
      seat_limit:
        'There are no seats at this table right now. The GM will see the request: if they add a seat, you can send it again through the same link.',
      byGm: 'The GM declined the request.',
    },
    rejectedHint:
      'This is not an error and nothing here is broken. If your GM sent you the link — write to them; if it was forwarded, you may have landed at the wrong table.',
    refused: 'This link does not work: it was revoked or mistyped.',
    recheck: 'Check again',
    seatsLeft: 'Seats left: {{left}}',
    full: 'No seats left right now. You can still send the request — the GM will see that someone did not fit.',
    pickCharacter: 'Who are you bringing',
    pickCharacterHint:
      'Take a character you already have or create a new one. They stay yours: if the GM declines, or frees the seat later, the character comes back to you whole.',
    mySeats: 'My seats',
    seatOf: '{{campaign}} · {{seat}}',
    archived: 'The campaign is archived',
    needCharacter: 'There is no character at this seat yet.',
    attach: 'A character from another device — by code',
    attachHint:
      'Enter the code of a character you made earlier on another device — they go into the request with all their items, levels and history.',
    code: 'Character code',
    attachSubmit: 'Take this character',
    newCharacter: 'Create a new one',
    deviceChange: 'I am on a new device',
    deviceChangeHint:
      'If you are already at the table and the device is new, the link will not help: it is for arriving with a new character. Enter your character code — the GM will see the request and let you back.',
    deviceChangeSubmit: 'Send the request',
    failed: 'That did not work. Try again',
  },

  auth: {
    signIn: 'Sign in',
    signUp: 'Game master registration',
    email: 'Email',
    password: 'Password',
    passwordHint:
      'At least {{count}} characters. No composition rules — length is what matters.',
    submitSignIn: 'Sign in',
    submitSignUp: 'Register',
    toSignUp: 'No account yet',
    toSignIn: 'Already have an account',
    forgot: 'Forgot your password',
    forgotTitle: 'Password recovery',
    forgotText: 'We will send a link to change the password. It works for one hour.',
    forgotSubmit: 'Send the link',
    sent: 'If we have that address, the message is already on its way. Check your inbox.',
    registered:
      'The message is sent. Open the link in it — signing in does not work before that.',
    confirmTitle: 'Email confirmation',
    confirmWorking: 'Confirming…',
    confirmDone: 'The address is confirmed. You can sign in now.',
    resetTitle: 'New password',
    resetSubmit: 'Change the password',
    resetDone: 'The password is changed and every session is closed. Sign in again.',
    signOut: 'Sign out',
    signOutAll: 'Sign out everywhere',
    signedInAs: 'Signed in as {{email}}',
    working: 'Sending…',
    resend: 'Send the letter again',
    resentDone: 'Sent again. It takes up to a minute — check the spam folder too.',
    problem: {
      invalid_credentials: 'The address or the password did not match',
      email_not_confirmed:
        'The address is not confirmed: open the link from the message',
      invalid_token: 'This link does not work: it expired or was already used',
      rate_limited: 'Too often. Try again in {{count}}s',
      session_expired: 'The session ended, sign in again',
    },
    field: {
      email: 'Email',
      password: 'Password',
    },
    invalid: {
      invalid_email: 'Email: this does not look like an address',
      password_too_short: 'Password: at least {{count}} characters',
      password_too_long: 'Password: too long',
      required: '{{field}}: fill this in',
      invalid: '{{field}}: this value did not work',
    },
    unreachable: 'The server is not answering. Check the connection and try again',
    serverFailed: 'Something broke on the server. We already know — try again later',
    failed: 'That did not work. Try again',
    adopt: {
      title: 'Move characters to the account',
      text: 'This device has characters without a campaign. Move them to the account? The move is one-way: a character cannot be handed back to the device.',
      submit: 'Move the selected ones',
      skip: 'Do not move',
      done: 'Moved: {{count}}',
    },
  },

  start: {
    title: 'Where do we start',
    createTitle: 'Create a character',
    createText:
      'A character without a campaign: abilities, the calculation and the sheet all work. A GM can pick it up later by its code.',
    create: 'Create a character',
    joinTitle: 'Join a campaign',
    joinText: 'Paste the link your GM sent you.',
    link: 'Link from the GM',
    join: 'Enter',
    mine: 'My characters: {{count}}',
    refusal: {
      empty: 'Paste the link from your GM',
      'not-a-link': 'This does not look like a link from your GM',
      'no-token': 'The link carries no invitation — it looks truncated',
    },
  },
  edit: {
    title: 'Edit choices',
    draftHint:
      'This character has no campaign, so the edit applies at once. Slots the new choice does not have will disappear; new ones arrive empty, and you cannot save without them.',
    seatedHint:
      'This character sits at a table, so the edit goes to the game master and takes effect once they decide. The sheet stays as it is until then.',
    classLocked:
      'At level {{level}} the class cannot change: level-ups depend on it. To change the class, undo the level-ups.',
    save: 'Save the edit',
    send: 'Send to the game master',
    open: 'Edit choices',
    openSeated: 'Ask for an edit',
    ownContent: 'Missing a record? Make your own',
    pending: 'The edit is waiting for the game master. The sheet is unchanged for now.',
    rejected: 'The game master declined the edit.',
    rejectedWhy: 'The game master declined the edit: {{note}}',
    changesTitle: 'What changes',
    noChanges: 'The edit changes nothing.',
    was: 'Was: {{value}}',
    will: 'Will be: {{value}}',
    empty: 'empty',
    line: {
      name: 'Name',
      class: 'Class',
      species: 'Species',
      background: 'Background',
      ability_method: 'Ability method',
      abilities: 'Ability scores',
      ability_increase: 'Background increases',
    },
  },
  whatsNew: {
    title: 'What’s new',
    hint: 'Recent changes, in short and without the technical words. The detailed change list is kept by the developers.',
    version: 'Version {{version}} · {{date}}',
    back: 'Back',
    items: {
      v0_4_12_1: 'The sheet can be printed: Export → Print. One A4 page laid out like the familiar paper sheet, in black and white.',
      v0_4_12_2: 'The paper shows the same numbers as the screen, plus subclass, origin feat and counters with boxes to tick in pencil. What the app does not track — alignment, death saves — is left blank to fill in by hand.',
      v0_4_12_3: 'Cantrip attacks name their damage type in Russian instead of showing a raw key.',
      v0_4_11_1: 'Resource counters on the sheet and on the game master’s panel show their translated names instead of the raw record name.',
      v0_4_11_2: 'A skill your background already gives no longer eats a choice: it is greyed out in the list, with a note saying where you got it.',
      v0_4_11_3: 'Every item now has a translated name — in the list the game master hands items from, and among the editor’s templates.',
      v0_4_11_4: 'Three monster weapons are now named the same as in the equipment list.',
      v0_4_11_5: 'This screen appeared.',
      v0_4_10_1: 'A campaign has a starting level. The character is still built step by step: creation, then level-ups one after another, right inside the join request.',
      v0_4_10_2: 'The game master sees one list of everything that does not match the campaign, with what it means and what can be done. A declined player is told why.',
      v0_4_10_3: 'Choices can be edited. A character without a campaign changes at once; one at a table goes through the game master.',
      v0_4_10_4: 'A player without an account can create their own record — say, a spell missing from the book. At the table it works once the game master accepts it.',
      v0_4_10_5: 'Names changed: one trait was called by two different words on two screens; now it has one name everywhere.',
      v0_4_9_1: 'Category names changed to match the dnd.su reference, so they are easier to look up.',
      v0_4_9_2: 'A feat chosen at creation now gives what it promises: the ones with their own choices ask for them.',
      v0_4_9_3: 'Rule links point to the dnd.su section for the 2024 edition.',
      v0_4_8_1: 'Unfamiliar terms got a question mark: tap it to see what the term means, with a link to dnd.su.',
    },
  },
  wizard: {
    title: 'Character creation',
    name: 'Character name',
    targetLevel: 'Starting level',
    targetOne: 'The character starts at level 1.',
    targetHint_one:
      'After creation a level-up opens: {{count}} more level. You can stop at any point — the character keeps the level it reached.',
    targetHint_few:
      'After creation level-ups open: {{count}} more levels. You can stop at any point — the character keeps the level it reached.',
    targetHint_many:
      'After creation level-ups open: {{count}} more levels. You can stop at any point — the character keeps the level it reached.',
    choose: 'Choose: {{count}}',
    search: 'Search the list',
    moreOptions: '{{count}} more in the list — use the search',
    chosen: 'Chosen: {{count}}',
    taken: {
      granted: 'already have it: {{source}}',
      picked: 'already chosen: {{slot}}',
    },
    submit: 'Create',
    cancel: 'Cancel',
    loading: 'Loading the creation plan…',
    draftNotice:
      'This character is created without a campaign. A GM can pick it up later by its code.',
    abilities: {
      title: 'Ability scores',
      method: 'Method',
      standard_array: 'Standard array',
      point_buy: 'Point buy',
      rolls: 'Rolls',
      budget: 'Points left: {{remaining}} of {{budget}}',
      overBudget: 'Over budget by {{over}}',
      poolTitle: 'Rolled values',
      poolRollsHint:
        'Six numbers: {{dice}}d{{die}}, keep the best {{keep}} — {{count}} times',
      poolFixedHint: 'The standard array is fixed by the rules',
      rollPool: 'Roll the dice',
      poolOrderHint: 'The order you enter them in changes nothing: you assign them',
      poolItem: 'Roll {{index}}',
      poolLeft: 'Left to assign: {{values}}',
      poolDone: 'Every value assigned',
      assignEmpty: 'not chosen',
    },
    increase: {
      title: 'Background increases',
      hint: 'Patterns: {{patterns}}',
      none: 'No increase',
      left: 'Left to hand out: {{values}}',
      done: 'All increases handed out',
    },
    equipment: {
      times: '{{count}} × {{name}}',
      gold: '{{count}} GP',
      last: '{{head}} and {{tail}}',
    },
    invalid: {
      title: 'Still missing',
      name: 'Enter the character name',
      slot: 'Choose {{count}}',
      abilities: 'Hand out all six values',
    },
    problems: {
      title: 'The server refused these choices',
      'unknown-slot': 'There is no such choice in the creation plan',
      'unknown-option': 'There is no such option here',
      'wrong-count': 'Choose {{count}}, chosen {{chosen}}',
      'duplicate-pick': 'The same option is chosen twice',
      'unknown-content': 'The chosen record is not in the catalogue',
      'not-standard-array': 'The values do not match the standard array',
      'point-buy-over-budget': 'More points spent than available',
      'score-not-purchasable': 'That score cannot be bought',
      'roll-out-of-range': 'That number cannot come up on this roll',
      'wrong-roll-count': 'Not as many numbers rolled as required',
      'assignment-mismatch': 'The numbers handed out are not the ones rolled',
      'increase-pattern': 'The rules do not allow this pattern of increases',
      'increase-not-offered':
        'The background does not offer an increase to that ability',
      'increase-over-max': 'The increase raises the ability above the limit',
      'class-locked':
        'The class can only change at level 1: level-ups depend on it. Undo them and level up again',
      'levels-broken':
        'Level-ups no longer fit after this edit: undo them and level up again',
      other: 'The choice was not accepted',
    },
  },
  update: {
    available: 'An update is available',
    reload: 'Reload',
  },
  abilities: {
    strength: 'Strength',
    dexterity: 'Dexterity',
    constitution: 'Constitution',
    intelligence: 'Intelligence',
    wisdom: 'Wisdom',
    charisma: 'Charisma',
  },
  abilitiesShort: {
    strength: 'STR',
    dexterity: 'DEX',
    constitution: 'CON',
    intelligence: 'INT',
    wisdom: 'WIS',
    charisma: 'CHA',
  },
  trait: {
    save: '{{name}} saving throws',
    skill: 'Skill: {{name}}',
    check: '{{ability}} checks: {{skill}}',
    condition: {
      charmed: 'Saving throws against Charmed',
      frightened: 'Saving throws against Frightened',
      poisoned: 'Saving throws against Poisoned',
    },
    armor: {
      light: 'Light armor',
      medium: 'Medium armor',
      heavy: 'Heavy armor',
      shields: 'Shields',
    },
    weapon: {
      simple: 'Simple weapons',
      martial: 'Martial weapons',
    },
    tool: {
      calligraphers_supplies: 'Calligrapher’s Supplies',
      herbalism_kit: 'Herbalism Kit',
      thieves_tools: 'Thieves’ Tools',
    },
    damage: {
      bludgeoning: 'Bludgeoning damage',
      piercing: 'Piercing damage',
      slashing: 'Slashing damage',
      cold: 'Cold',
      fire: 'Fire',
      force: 'Force',
      poison: 'Poison',
      acid: 'Acid',
      lightning: 'Lightning',
      necrotic: 'Necrotic',
      psychic: 'Psychic',
      radiant: 'Radiant',
      thunder: 'Thunder',
    },
  },
  notFound: {
    title: 'No such address',
    text: 'We do not know what to show at {{path}}.',
    home: 'Back to characters',
  },
  errors: {
    network: 'The server is unreachable. Changes need the network (D9).',
    rejected: 'The server did not accept the entry. Check the fields.',
    badFields: 'Fields not accepted: {{fields}}.',
    serverFailed: 'The server answered with an error. That is on our side.',
    unknown: 'Something went wrong.',
    calculation: {
      title: 'The sheet did not add up',
      'unknown-path': 'The calculation has no value named “{{path}}”.',
      'cyclic-ref': 'The values reference each other in a circle: {{chain}}.',
      'conflicting-set': 'Several sources set “{{target}}” at once.',
      'missing-base': '“{{target}}” has bonuses but no base value.',
      'duplicate-step': 'The journal has two steps numbered {{stepIndex}}.',
      'unknown-content':
        'The journal points at entry “{{id}}”, which no longer exists.',
      'unexpected-target-kind':
        'Operations of different kinds are mixed over “{{target}}”.',
      'unexpected-context-value': 'The value at “{{path}}” is of the wrong kind.',
      unknown: 'The reason could not be named.',
    },
  },
} as const

/**
 * Ресурсы i18next: словари интерфейса плюс названия записей контента
 * под `content.*`. Термины лежат отдельным файлом, потому что это
 * перевод контента, а не строк интерфейса, и набор ключей у них
 * задаётся самим SRD.
 */
/**
 * Слияние словаря интерфейса с собранными словарями контента.
 *
 * Собранные пространства кладутся поверх, и строка интерфейса,
 * попавшая в занятое ими имя, молча пропадала: кнопка «Оригинал»
 * лежала в `content` и показывала на листе «content.original».
 * Пересечение имён — ошибка сборки словаря, а не сюрприз на экране.
 */
export function mergeDictionaries(
  base: Record<string, unknown>,
  generated: Record<string, unknown>,
): Record<string, unknown> {
  for (const key of Object.keys(generated)) {
    if (key in base) {
      throw new Error(`словарь интерфейса уже занял пространство «${key}»`)
    }
  }
  return { ...base, ...generated }
}

const generatedFor = (language: Language) => ({
  content: termsFor(language),
  // Названия особенностей классов, подклассов и видов
  // (`feature-terms.ts`). Отдельной веткой от `content`: там ключ —
  // запись контента, здесь — особенность внутри записи.
  feature: featureTermsFor(language),
  slot: slotTitlesFor(language),
  slotOption: slotOptionsFor(language),
  equipment: equipmentNamesFor(language),
  // Обороты из параметров заклинаний: материальные компоненты
  // и условия реакций (`spell-phrases.ts`).
  spellPhrase: spellPhrasesFor(language),
})

/**
 * Языки перечислены отдельно, а не выведены из `resources`: словари
 * контента собираются по языку, и вывод типа из объекта, который сам
 * этим типом пользуется, замыкается на себя.
 */
export const LANGUAGES = ['ru', 'en'] as const

export type Language = (typeof LANGUAGES)[number]

export const resources = {
  ru: { translation: mergeDictionaries(ru, generatedFor('ru')) },
  en: { translation: mergeDictionaries(en, generatedFor('en')) },
}

const FALLBACK: Language = 'en'

/** Язык браузера сводится к поддерживаемому: `ru-RU` → `ru`. */
export function pickLanguage(browserLanguage: string | undefined): Language {
  const base = browserLanguage?.split('-')[0]?.toLowerCase()
  return base !== undefined && base in resources ? (base as Language) : FALLBACK
}

/**
 * Плоский список ключей словаря: `wizard.abilities.budget`.
 *
 * Нужен тесту, который сверяет языки между собой. Сравнивать словари
 * целиком нельзя — значения у них по определению разные; сравнивается
 * именно набор ключей.
 */
export function flattenKeys(dictionary: unknown, prefix = ''): string[] {
  if (typeof dictionary !== 'object' || dictionary === null) return [prefix]

  const keys: string[] = []
  for (const [key, value] of Object.entries(dictionary)) {
    keys.push(...flattenKeys(value, prefix === '' ? key : `${prefix}.${key}`))
  }
  return keys.sort()
}

/**
 * Смена языка на лету (D11).
 *
 * Возвращает новую функцию перевода: контекст отдаёт компонентам
 * именно её, и перерисовка с новым `t` — единственное, что нужно
 * сделать сверху.
 */
export async function changeLanguage(language: Language): Promise<TFunction> {
  return i18next.changeLanguage(language)
}

export async function initI18n(browserLanguage?: string): Promise<TFunction> {
  return i18next.init({
    resources,
    lng: pickLanguage(browserLanguage),
    fallbackLng: FALLBACK,
    interpolation: {
      // Экранирование под HTML не нужно: значения подставляются
      // в textContent, а не в разметку.
      escapeValue: false,
    },
  })
}
